import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowRight, ArrowLeft, Loader2, User as UserIcon,
    LogOut, Briefcase, ChevronRight, Search, X, Users, GraduationCap,
    Network, Building2, Zap, ExternalLink, ChevronDown, ChevronUp,
    Lock, Sparkles, AlertCircle, Inbox, PlusCircle, MapPin,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import FeedbackHeaderCTA from './FeedbackHeaderCTA';
import { RolePickerModal, LocationPickerModal } from './Pickers';
import { exportAggregatedJobMatches, getCompanyInsights, getProfile, captureOrgDestinationMap, precheckOrgDestinationMapCapture } from '../api/insights';
import { toast } from 'sonner';
import { readInsightsProgress } from '../utils/insightsProgress';
const INSIGHTS_STATE_STORAGE_KEY = 'insightsDashboardState';
const INSIGHTS_CACHE_PREFIX = 'fairhire_insights_cache:';
const getDismissedNewTagsStorageKey = (userId) => `insights_dismissed_new_tags_${userId || 'anon'}`;
const FIRST_SESSION_NUDGE_MS = 6 * 60 * 1000;
const getProgressFirstSessionStartKey = (userId) => `insights_progress_first_session_start_${userId || 'anon'}`;
const getProgressFirstSessionDoneKey = (userId) => `insights_progress_first_session_done_${userId || 'anon'}`;
const getViewportWidth = () => {
    if (typeof window === 'undefined') return 1024;
    return Math.min(
        window.innerWidth || 1024,
        window.visualViewport?.width || window.innerWidth || 1024
    );
};
const readDismissedNewTags = (userId) => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = localStorage.getItem(getDismissedNewTagsStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};
const writeDismissedNewTags = (userId, data) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(getDismissedNewTagsStorageKey(userId), JSON.stringify(data || {}));
    } catch {
        // ignore storage failures
    }
};
const FeatureNewBadge = ({ isDark, tooltip }) => {
    const [open, setOpen] = useState(false);
    return (
        <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4 }}
        >
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: '0.05em',
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: isDark ? 'linear-gradient(135deg, rgba(16,185,129,0.26), rgba(5,150,105,0.18))' : 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.1))',
                    border: isDark ? '1px solid rgba(52,211,153,0.55)' : '1px solid rgba(5,150,105,0.34)',
                    color: isDark ? '#6EE7B7' : '#065F46',
                    textTransform: 'uppercase',
                    boxShadow: isDark ? '0 4px 14px rgba(16,185,129,0.16)' : '0 4px 12px rgba(16,185,129,0.12)',
                }}
            >
                New
            </span>
            <span
                aria-hidden
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    marginLeft: 4,
                    background: isDark ? '#34D399' : '#059669',
                    boxShadow: isDark ? '0 0 10px rgba(52,211,153,0.7)' : '0 0 8px rgba(5,150,105,0.45)',
                }}
            />
            {open && (
                <span
                    style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        right: 0,
                        width: 250,
                        zIndex: 90,
                        padding: '8px 10px',
                        borderRadius: 10,
                        background: isDark ? '#111827' : '#ffffff',
                        border: isDark ? '1px solid rgba(52,211,153,0.35)' : '1px solid rgba(5,150,105,0.25)',
                        boxShadow: isDark ? '0 16px 30px rgba(0,0,0,0.42)' : '0 12px 24px rgba(15,23,42,0.18)',
                        color: isDark ? 'rgba(255,255,255,0.9)' : '#334155',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        letterSpacing: '0.01em',
                    }}
                >
                    {tooltip}
                </span>
            )}
        </span>
    );
};
const getCachedInsights = (orgId, params) => {
    try {
        const key = `${INSIGHTS_CACHE_PREFIX}${orgId}:${JSON.stringify(params)}`;
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const { timestamp, data } = JSON.parse(raw);
        // TTL: 15 minutes
        if (Date.now() - timestamp > 15 * 60 * 1000) {
            sessionStorage.removeItem(key);
            return null;
        }
        return data;
    } catch (e) {
        return null;
    }
};
const setCachedInsights = (orgId, params, data) => {
    try {
        const key = `${INSIGHTS_CACHE_PREFIX}${orgId}:${JSON.stringify(params)}`;
        sessionStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
    } catch (e) {
        // Silently fail if storage full
    }
};
const getStateKey = (u) =>
    `${INSIGHTS_STATE_STORAGE_KEY}:${String(u?.id || u?.email || 'anonymous')}`;
/* ── small helpers ── */
const initials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const avatarColor = (name = '') => {
    const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return cols[Math.abs(h) % cols.length];
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const getProgressNudgeState = (apps, referrals, appAvg = 11, refAvg = 8) => {
    const appRatio = appAvg > 0 ? apps / appAvg : 0;
    const refRatio = refAvg > 0 ? referrals / refAvg : 0;
    const ratio = (appRatio + refRatio) / 2;
    if (ratio >= 1) {
        return {
            tone: 'caught_up',
            color: '#10B981',
            message: `<strong style="color:#10B981;font-weight:600;">You're at the avg</strong> - ${apps} apps sent, ${referrals} referral paths explored. You're in the game.`,
        };
    }
    if (ratio >= 0.5) {
        return {
            tone: 'halfway',
            color: '#2563EB',
            message: `Getting there - <strong style="color:#2563EB;font-weight:600;">${apps} apps sent, ${referrals} referral paths explored</strong>. On avg, users like you have done ${appAvg} and ${refAvg}.`,
        };
    }
    return {
        tone: 'just_started',
        color: '#EF4444',
        message: `You've sent <strong style="color:#EF4444;font-weight:600;">${apps} of ${appAvg} avg applications</strong> - most people have already explored ${refAvg} referral paths.`,
    };
};
/* ── tiny building blocks — all theme-aware ── */
const Card = ({ children, onClick, style = {}, className = '' }) => {
    const { isDark } = useTheme();
    const acc = isDark ? 'rgba(245,158,11,0.35)' : 'rgba(59,130,246,0.4)';
    const bg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
    const bdr = isDark ? 'rgba(255,255,255,0.09)' : '#e2e8f0';
    return (
        <motion.div
            onClick={onClick}
            className={className}
            initial={false}
            whileHover={onClick ? {
                y: -2,
                borderColor: acc,
                boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 24px rgba(59,130,246,0.12)',
            } : {}}
            transition={{ type: 'tween', duration: 0.2 }}
            style={{
                background: bg,
                border: `1px solid ${bdr}`,
                borderRadius: 20,
                padding: 0,
                cursor: onClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                ...style,
            }}
        >
            {children}
        </motion.div>
    );
};
const CardTitle = ({ children, subtitle, icon, color, eyebrow }) => {
    const { isDark } = useTheme();
    const txtPrimary = isDark ? '#fff' : '#0f172a';
    const txtMuted = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    return (
        <div style={{ marginBottom: 10 }}>
            {eyebrow && (
                <span style={{
                    fontSize: 10, fontWeight: 800, color: color || (isDark ? '#F59E0B' : '#2563eb'),
                    textTransform: 'uppercase', letterSpacing: '0.12em',
                    display: 'block', marginBottom: 8
                }}>
                    {eyebrow}
                </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                {icon && (
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: (color || (isDark ? '#F59E0B' : '#2563eb')) + '18',
                        border: `1px solid ${(color || (isDark ? '#F59E0B' : '#2563eb'))}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: color || (isDark ? '#F59E0B' : '#2563eb'), flexShrink: 0
                    }}>{icon}</div>
                )}
                <h3 style={{
                    fontSize: 14, fontWeight: 700, color: txtPrimary,
                    fontFamily: icon ? "'Playfair Display', serif" : "'Manrope', sans-serif",
                    lineHeight: 1.4, flex: 1, margin: 0
                }}>{children}</h3>
            </div>
            {subtitle && <p style={{ fontSize: 11, color: txtMuted, lineHeight: 1.5, margin: 0 }}>{subtitle}</p>}
        </div>
    );
};
/* ── Shared card-level empty state ── */
const EmptyCardState = ({ message = 'No data available yet.', hint = null }) => {
    const { isDark } = useTheme();
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '22px 12px', gap: 10, textAlign: 'center',
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Inbox size={17} color={isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8'} />
            </div>
            <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b', margin: 0, lineHeight: 1.5 }}>
                {message}
            </p>
            {hint && (
                <p style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.22)' : '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                    {hint}
                </p>
            )}
        </div>
    );
};
const CompanyRow = ({ rank, name, rightLabel, rightColor = '#F59E0B', onClick }) => {
    const { isDark } = useTheme();
    const col = avatarColor(name);
    const hoverBg = isDark ? 'rgba(245,158,11,0.04)' : 'rgba(59,130,246,0.05)';
    const txtMain = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';
    const txtMuted = isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8';
    const divider = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
    return (
        <motion.div
            onClick={onClick}
            initial={false}
            whileHover={onClick ? { background: hoverBg } : {}}
            transition={{ duration: 0.15 }}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '9px 10px',
                margin: '0 -10px',
                borderRadius: 10,
                borderBottom: `1px solid ${divider}`,
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            {rank != null && (
                <span style={{ fontSize: 11, color: txtMuted, width: 18, flexShrink: 0 }}>#{rank}</span>
            )}
            <div style={{
                width: 30, height: 30, borderRadius: 8, background: `${col}22`,
                border: `1px solid ${col}44`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 12, fontWeight: 700, color: col, flexShrink: 0
            }}>{name[0]?.toUpperCase() || '?'}</div>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: txtMain }}>{name}</span>
            {rightLabel && (
                <span style={{ fontSize: 13, fontWeight: 700, color: rightColor, flexShrink: 0 }}>{rightLabel}</span>
            )}
            {onClick && <ChevronRight size={14} style={{ color: txtMuted, flexShrink: 0 }} />}
        </motion.div>
    );
};
const SeeMore = ({ label, onClick, color }) => {
    const { isDark } = useTheme();
    const defaultColor = isDark ? '#F59E0B' : '#2563eb';
    const c = color || defaultColor;
    const hoverColor = isDark ? '#fff' : '#1e293b';
    const divider = isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0';
    return (
        <motion.button
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            initial={false}
            whileHover={{ color: hoverColor }}
            transition={{ duration: 0.15 }}
            style={{
                marginTop: 'auto',
                paddingTop: 14,
                borderTop: `1px solid ${divider}`,
                fontSize: 12,
                fontWeight: 700,
                color: c,
                background: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: 'none',
                cursor: 'pointer',
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 4,
                paddingBottom: 0,
            }}
        >
            {label} <ArrowRight size={13} style={{ marginLeft: 'auto' }} />
        </motion.button>
    );
};
const InnerBox = ({ children }) => {
    const { isDark } = useTheme();
    const divider = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
    return (
        <div style={{
            padding: '10px 0 0',
            marginTop: 8,
            borderTop: `1px solid ${divider}`,
        }}>
            {children}
        </div>
    );
};
/* ── Card Tag Badge ── */
const CardTag = ({ label, color }) => {
    const { isDark } = useTheme();
    const tagBg = isDark ? `${color}18` : `${color}14`;
    const tagBorder = isDark ? `${color}35` : `${color}30`;
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            width: 'fit-content',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1.2,
            color: color,
            background: tagBg,
            border: `1px solid ${tagBorder}`,
            letterSpacing: '0.02em',
            marginBottom: 8,
        }}>{label}</span>
    );
};
/* ── Card Illustration Components ── */
const CardIllustration = ({ children, bgGradient }) => {
    const { isDark } = useTheme();
    return (
        <div style={{
            width: '100%',
            height: 120,
            background: isDark
                ? (bgGradient || 'linear-gradient(135deg, rgba(30,30,30,0.9), rgba(20,20,20,0.95))')
                : (bgGradient || 'linear-gradient(135deg, #f0f4f8, #e2e8f0)'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
        }}>
            {children}
        </div>
    );
};
/* ── Card Content Wrapper (padding for content below illustration) ── */
const CardContent = ({ children }) => (
    <div style={{
        padding: '14px 20px 18px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    }}>
        {children}
    </div>
);
/* 1: Hiring right now – Pulsing radar with LIVE badge (from 01-live-hiring.svg) */
const IllustrationHiringNow = () => {
    const { isDark } = useTheme();
    const coreColor = isDark ? '#10B981' : '#D04020';
    const coreLighter = isDark ? '#34D399' : '#FF6B3D';
    const ringColor = isDark ? '#10B981' : '#E86840';
    const gridLine = isDark ? 'rgba(255,255,255,0.06)' : '#F0957A';
    const refRing = isDark ? 'rgba(255,255,255,0.08)' : '#EDBAA8';
    const badgeBg = isDark ? '#dc2626' : '#C03015';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0a120e)' : 'radial-gradient(circle, #FFF4F0, #FDE8E0)'}>
            <svg width="100%" height="100%" viewBox="40 20 320 230" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <style>{`
                        @keyframes pulse1{0%,100%{r:36;opacity:.18}50%{r:60;opacity:0}}
                        @keyframes pulse2{0%,100%{r:24;opacity:.28}50%{r:48;opacity:.05}}
                        @keyframes pulse3{0%,100%{r:14;opacity:.38}50%{r:38;opacity:.1}}
                        @keyframes badgePulse{0%,100%{opacity:1}50%{opacity:.7}}
                        .ci-r1{animation:pulse1 2.4s ease-in-out infinite}
                        .ci-r2{animation:pulse2 2.4s ease-in-out infinite .4s}
                        .ci-r3{animation:pulse3 2.4s ease-in-out infinite .8s}
                        .ci-bp{animation:badgePulse 2s ease-in-out infinite}
                    `}</style>
                </defs>
                <line x1="200" y1="20" x2="200" y2="240" stroke={gridLine} strokeWidth="0.5" opacity="0.3" />
                <line x1="40" y1="140" x2="360" y2="140" stroke={gridLine} strokeWidth="0.5" opacity="0.3" />
                <circle cx="200" cy="140" r="100" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle cx="200" cy="140" r="70" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle cx="200" cy="140" r="40" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle className="ci-r1" cx="200" cy="140" r="36" fill={ringColor} opacity="0.18" />
                <circle className="ci-r2" cx="200" cy="140" r="24" fill={ringColor} opacity="0.28" />
                <circle className="ci-r3" cx="200" cy="140" r="14" fill={ringColor} opacity="0.38" />
                <circle cx="200" cy="140" r="20" fill={coreColor} />
                <circle cx="200" cy="140" r="9" fill={coreLighter} />
                <circle cx="197" cy="137" r="3" fill="white" opacity="0.5" />
                <rect x="218" y="118" width="46" height="20" rx="10" fill={badgeBg} />
                <text className="ci-bp" x="241" y="132" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill="white" letterSpacing="1">LIVE</text>
            </svg>
        </CardIllustration>
    );
};
/* 2: Easy referral – Alumni network density (from 02-alumni-density.svg) */
const IllustrationReferral = () => {
    const { isDark } = useTheme();
    const nodeMain = isDark ? '#10B981' : '#3B9A54';
    const nodeOther = isDark ? '#059669' : '#4CAF60';
    const nodeHighlight = isDark ? '#34D399' : '#7DD48A';
    const lineColor = isDark ? '#34D399' : '#6BBD7E';
    const outerRing = isDark ? '#065F46' : '#CCEBBF';
    const badgeBg = isDark ? '#059669' : '#2E7D40';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0a110d)' : 'radial-gradient(circle, #F4FAF0, #E6F3DC)'}>
            <svg width="100%" height="100%" viewBox="50 30 300 240" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <style>{`
                        @keyframes nodeApp{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.8;transform:scale(1.06)}}
                        .ci-n1{animation:nodeApp 3s ease-in-out infinite 0s;transform-origin:200px 145px}
                        .ci-n2{animation:nodeApp 3s ease-in-out infinite .5s;transform-origin:130px 100px}
                        .ci-n3{animation:nodeApp 3s ease-in-out infinite 1s;transform-origin:270px 100px}
                        .ci-n4{animation:nodeApp 3s ease-in-out infinite 1.5s;transform-origin:150px 195px}
                        .ci-n5{animation:nodeApp 3s ease-in-out infinite 2s;transform-origin:250px 195px}
                    `}</style>
                </defs>
                <line x1="200" y1="145" x2="130" y2="100" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="270" y2="100" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="150" y2="195" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="250" y2="195" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="130" y1="100" x2="270" y2="100" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="130" y1="100" x2="150" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="270" y1="100" x2="250" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="150" y1="195" x2="250" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <g className="ci-n1">
                    <circle cx="200" cy="145" r="26" fill={outerRing} opacity="0.5" />
                    <circle cx="200" cy="145" r="20" fill={nodeMain} />
                    <circle cx="200" cy="133" r="7" fill={nodeHighlight} />
                    <path d="M185 155 Q185 148 200 148 Q215 148 215 155" fill={nodeHighlight} />
                </g>
                <g className="ci-n2">
                    <circle cx="130" cy="100" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="130" cy="90" r="6" fill={nodeHighlight} />
                    <path d="M118 108 Q118 102 130 102 Q142 102 142 108" fill={nodeHighlight} />
                </g>
                <g className="ci-n3">
                    <circle cx="270" cy="100" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="270" cy="90" r="6" fill={nodeHighlight} />
                    <path d="M258 108 Q258 102 270 102 Q282 102 282 108" fill={nodeHighlight} />
                </g>
                <g className="ci-n4">
                    <circle cx="150" cy="195" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="150" cy="185" r="6" fill={nodeHighlight} />
                    <path d="M138 203 Q138 197 150 197 Q162 197 162 203" fill={nodeHighlight} />
                </g>
                <g className="ci-n5">
                    <circle cx="250" cy="195" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="250" cy="185" r="6" fill={nodeHighlight} />
                    <path d="M238 203 Q238 197 250 197 Q262 197 262 203" fill={nodeHighlight} />
                </g>
                <rect x="153" y="228" width="94" height="20" rx="10" fill={badgeBg} />
                <text x="200" y="242" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9.5" fontWeight="700" fill="white">4 alumni inside</text>
            </svg>
        </CardIllustration>
    );
};
/* 3: Plan ahead – Stepping stone career path (from 03-stepping-stone.svg) */
const IllustrationCareerPath = () => {
    const { isDark } = useTheme();
    const pathColor = isDark ? '#a78bfa' : '#9087D8';
    const youBg = isDark ? '#374151' : '#DDDAF8';
    const youBorder = isDark ? '#6d28d9' : '#9087D8';
    const youText = isDark ? '#c4b5fd' : '#3A31A0';
    const job1Bg = isDark ? '#4c1d95' : '#B8B2F0';
    const job1Border = isDark ? '#7c3aed' : '#7068CC';
    const job1Text = isDark ? '#e9d5ff' : '#1E1870';
    const dreamColor1 = isDark ? '#7c3aed' : '#7B6DE8';
    const dreamColor2 = isDark ? '#5b21b6' : '#4A3CA8';
    const arrowColor = isDark ? '#8b5cf6' : '#6B5EC8';
    const sparkle = isDark ? '#a78bfa' : '#B8B2F0';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #110e18)' : 'radial-gradient(circle, #F5F4FE, #ECEAFE)'}>
            <svg width="100%" height="100%" viewBox="20 50 360 200" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <radialGradient id="ciDreamGrad" cx="40%" cy="35%" r="60%">
                        <stop offset="0%" stopColor={dreamColor1} />
                        <stop offset="100%" stopColor={dreamColor2} />
                    </radialGradient>
                    <style>{`
                        @keyframes starP{0%,100%{opacity:1}50%{opacity:.4}}
                        .ci-star{animation:starP 2s ease-in-out infinite}
                    `}</style>
                </defs>
                <path d="M 72 210 Q 150 170 200 148 Q 250 126 310 85" fill="none" stroke={pathColor} strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                <polygon points="305,78 318,88 308,96" fill={arrowColor} opacity="0.7" />
                <circle cx="72" cy="210" r="24" fill={youBg} stroke={youBorder} strokeWidth="1.5" />
                <text x="72" y="207" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="600" fill={youText}>You</text>
                <text x="72" y="219" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="7.5" fill={youText} opacity="0.8">now</text>
                <circle cx="200" cy="148" r="26" fill={job1Bg} stroke={job1Border} strokeWidth="1.5" />
                <text x="200" y="144" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="600" fill={job1Text}>Job 1</text>
                <text x="200" y="156" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="7.5" fill={job1Text} opacity="0.8">1st move</text>
                <circle cx="315" cy="85" r="32" fill="url(#ciDreamGrad)" stroke={dreamColor2} strokeWidth="1.5" />
                <text x="315" y="81" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill="white">Dream</text>
                <text x="315" y="94" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="8" fill="#e9d5ff">job</text>
                <circle className="ci-star" cx="350" cy="58" r="3" fill={sparkle} />
                <circle className="ci-star" cx="280" cy="55" r="2" fill={sparkle} style={{ animationDelay: '0.6s' }} />
                <circle className="ci-star" cx="355" cy="100" r="2.5" fill={sparkle} style={{ animationDelay: '1.2s' }} />
            </svg>
        </CardIllustration>
    );
};
/* 4: Stable workplace – Retention bar chart (from 04-retention-tenure.svg) */
const IllustrationTenure = () => {
    const { isDark } = useTheme();
    const bar1 = isDark ? '#D97706' : '#F5C060';
    const bar1b = isDark ? '#b45309' : '#E8A835';
    const bar2 = isDark ? '#d97706' : '#EFA030';
    const bar2b = isDark ? '#92400e' : '#D88818';
    const bar3 = isDark ? '#b45309' : '#E08520';
    const bar3b = isDark ? '#78350f' : '#C07010';
    const bar4 = isDark ? '#92400e' : '#C86810';
    const bar4b = isDark ? '#451a03' : '#A05008';
    const baseline = isDark ? 'rgba(255,255,255,0.12)' : '#D49030';
    const gridLine = isDark ? 'rgba(255,255,255,0.05)' : '#D49030';
    const xLabel = isDark ? 'rgba(255,255,255,0.45)' : '#8A5A10';
    const xLabelBold = isDark ? 'rgba(255,255,255,0.7)' : '#5C3808';
    const goodBg = isDark ? '#059669' : '#E8F6E8';
    const goodStroke = isDark ? '#10B981' : '#4A9A4A';
    const goodArrow = isDark ? '#34D399' : '#2E7A2E';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #15110a)' : 'radial-gradient(circle, #FFFAF3, #FEF2DC)'}>
            <svg width="100%" height="100%" viewBox="40 50 340 200" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="ciBg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar1} /><stop offset="100%" stopColor={bar1b} /></linearGradient>
                    <linearGradient id="ciBg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar2} /><stop offset="100%" stopColor={bar2b} /></linearGradient>
                    <linearGradient id="ciBg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar3} /><stop offset="100%" stopColor={bar3b} /></linearGradient>
                    <linearGradient id="ciBg4" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar4} /><stop offset="100%" stopColor={bar4b} /></linearGradient>
                    <style>{`
                        @keyframes ciGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}
                        .ci-b1{animation:ciGrow .6s ease-out .1s both;transform-origin:50% 100%}
                        .ci-b2{animation:ciGrow .6s ease-out .3s both;transform-origin:50% 100%}
                        .ci-b3{animation:ciGrow .6s ease-out .5s both;transform-origin:50% 100%}
                        .ci-b4{animation:ciGrow .6s ease-out .7s both;transform-origin:50% 100%}
                    `}</style>
                </defs>
                <line x1="65" y1="210" x2="320" y2="210" stroke={baseline} strokeWidth="1" opacity="0.4" />
                <line x1="65" y1="175" x2="320" y2="175" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <line x1="65" y1="140" x2="320" y2="140" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <line x1="65" y1="105" x2="320" y2="105" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <g className="ci-b1"><rect x="80" y="188" width="40" height="22" rx="4" fill="url(#ciBg1)" /></g>
                <g className="ci-b2"><rect x="140" y="162" width="40" height="48" rx="4" fill="url(#ciBg2)" /></g>
                <g className="ci-b3"><rect x="200" y="128" width="40" height="82" rx="4" fill="url(#ciBg3)" /></g>
                <g className="ci-b4"><rect x="260" y="88" width="40" height="122" rx="4" fill="url(#ciBg4)" /></g>
                <text x="100" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>6mo</text>
                <text x="160" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>1yr</text>
                <text x="220" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>1.5yr</text>
                <text x="280" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="700" fill={xLabelBold}>2yr+</text>
                <g opacity="0.85">
                    <circle cx="340" cy="120" r="16" fill={goodBg} stroke={goodStroke} strokeWidth="1" />
                    <polyline points="333,124 340,113 347,124" fill="none" stroke={goodArrow} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="340" y1="113" x2="340" y2="128" stroke={goodArrow} strokeWidth="2.2" strokeLinecap="round" />
                </g>
            </svg>
        </CardIllustration>
    );
};
/* 5: Your role only – Funnel filter (from 05-role-filter.svg) */
const IllustrationRoleFunnel = ({ roleLabel }) => {
    const { isDark } = useTheme();
    const topFill = isDark ? '#1e3a5f' : '#A8DFC8';
    const topFill2 = isDark ? '#1e4068' : '#60C4A0';
    const midFill = isDark ? '#2563eb' : '#38B08A';
    const midFill2 = isDark ? '#1d4ed8' : '#1A9070';
    const botFill = isDark ? '#1e40af' : '#108860';
    const botFill2 = isDark ? '#1e3a8a' : '#0A6848';
    const outlineColor = isDark ? '#3b82f6' : '#0A7058';
    const topText = isDark ? '#93c5fd' : '#0A5040';
    const midText = isDark ? 'rgba(255,255,255,0.6)' : '#084038';
    const botText = isDark ? '#fff' : '#E8FFF8';
    const dotColor = isDark ? '#60a5fa' : '#9EE8CE';
    const countColor = isDark ? '#93c5fd' : '#0A6848';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0d1220)' : 'radial-gradient(circle, #F0FAF7, #E0F5EE)'}>
            <svg width="100%" height="100%" viewBox="60 30 280 220" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="ciFT" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={topFill} /><stop offset="100%" stopColor={topFill2} /></linearGradient>
                    <linearGradient id="ciFM" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={midFill} /><stop offset="100%" stopColor={midFill2} /></linearGradient>
                    <linearGradient id="ciFB" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={botFill} /><stop offset="100%" stopColor={botFill2} /></linearGradient>
                    <style>{`
                        @keyframes ciDrop{0%{opacity:0;transform:translateY(-8px)}20%{opacity:1;transform:translateY(0)}80%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(6px)}}
                        .ci-d1{animation:ciDrop 2.4s ease-in-out infinite 0s}
                        .ci-d2{animation:ciDrop 2.4s ease-in-out infinite .4s}
                        .ci-d3{animation:ciDrop 2.4s ease-in-out infinite .8s}
                    `}</style>
                </defs>
                <rect x="98" y="50" width="204" height="32" rx="5" fill="url(#ciFT)" />
                <polygon points="98,82 302,82 252,128 148,128" fill="url(#ciFM)" />
                <rect x="148" y="128" width="104" height="34" fill="url(#ciFM)" />
                <rect x="170" y="162" width="60" height="28" rx="5" fill="url(#ciFB)" />
                <path d="M98 53 L302 53 L302 82 L252 128 L252 162 L230 162 L230 190 L170 190 L170 162 L148 162 L148 128 L98 82 Z" fill="none" stroke={outlineColor} strokeWidth="1" opacity="0.3" />
                <text x="200" y="72" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="600" fill={topText}>All companies (129)</text>
                <text x="200" y="114" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={midText} opacity="0.9">Filter by your role</text>
                <text x="200" y="152" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={botText} fontWeight="600">{roleLabel || 'PM'} hires only</text>
                <circle className="ci-d1" cx="178" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <circle className="ci-d2" cx="196" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <circle className="ci-d3" cx="214" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <text x="200" y="220" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill={countColor}>18 companies</text>
            </svg>
        </CardIllustration>
    );
};
/* 6: Jobs near you – Map pin with ripple rings (from 06-location.svg) */
const IllustrationLocation = () => {
    const { isDark } = useTheme();
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : '#A8C8E8';
    const ringStroke = isDark ? '#0ea5e9' : '#3A88D0';
    const pinColor1 = isDark ? '#0ea5e9' : '#4A9FE0';
    const pinColor2 = isDark ? '#0284c7' : '#1A6BB8';
    const dotLarge = isDark ? '#1e3a5f' : '#85B7EB';
    const dotSmall = isDark ? '#1e293b' : '#B5D4F4';
    const dotTiny = isDark ? '#1e293b' : '#C8DFF8';
    const shadowColor = isDark ? 'rgba(14,165,233,0.15)' : '#8AC4F0';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0d1018)' : 'radial-gradient(circle, #F2F8FE, #E2F0FC)'}>
            <svg width="100%" height="100%" viewBox="40 30 320 210" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <radialGradient id="ciPinGrad" cx="40%" cy="35%" r="60%">
                        <stop offset="0%" stopColor={pinColor1} />
                        <stop offset="100%" stopColor={pinColor2} />
                    </radialGradient>
                    <linearGradient id="ciShadow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={shadowColor} stopOpacity="0.4" />
                        <stop offset="100%" stopColor={shadowColor} stopOpacity="0" />
                    </linearGradient>
                    <style>{`
                        @keyframes ciRing{0%{r:8;opacity:.5}100%{r:65;opacity:0}}
                        @keyframes ciBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
                        .ci-rng{animation:ciRing 2.6s ease-out infinite}
                        .ci-rng2{animation:ciRing 2.6s ease-out infinite .9s}
                        .ci-pin{animation:ciBounce 3s ease-in-out infinite;transform-origin:200px 135px}
                    `}</style>
                </defs>
                {[60, 90, 120, 150, 180, 210].map(y => <line key={`h${y}`} x1="60" y1={y} x2="340" y2={y} stroke={gridColor} strokeWidth="0.4" opacity="0.5" />)}
                {[80, 130, 180, 230, 280, 330].map(x => <line key={`v${x}`} x1={x} y1="40" x2={x} y2="220" stroke={gridColor} strokeWidth="0.4" opacity="0.5" />)}
                <circle className="ci-rng" cx="200" cy="150" r="8" fill="none" stroke={ringStroke} strokeWidth="1.5" />
                <circle className="ci-rng2" cx="200" cy="150" r="8" fill="none" stroke={ringStroke} strokeWidth="1.5" />
                <ellipse cx="200" cy="208" rx="24" ry="8" fill="url(#ciShadow)" />
                <circle cx="140" cy="112" r="5" fill={dotLarge} opacity="0.7" />
                <circle cx="265" cy="100" r="5" fill={dotLarge} opacity="0.7" />
                <circle cx="120" cy="168" r="4.5" fill={dotSmall} opacity="0.6" />
                <circle cx="290" cy="165" r="4.5" fill={dotSmall} opacity="0.6" />
                <circle cx="255" cy="180" r="4" fill={dotSmall} opacity="0.6" />
                <circle cx="155" cy="76" r="3.5" fill={dotTiny} opacity="0.5" />
                <g className="ci-pin">
                    <path d="M200 88 C183 88 170 101 170 117 C170 140 200 170 200 170 C200 170 230 140 230 117 C230 101 217 88 200 88 Z" fill="url(#ciPinGrad)" />
                    <circle cx="200" cy="114" r="10" fill="white" opacity="0.95" />
                    <circle cx="200" cy="114" r="5.5" fill={pinColor2} />
                    <circle cx="197" cy="111" r="2.2" fill="white" opacity="0.6" />
                </g>
            </svg>
        </CardIllustration>
    );
};
const mergeCareerPathList = (careerPaths) => {
    const hop2 = Array.isArray(careerPaths?.hop2) ? careerPaths.hop2 : [];
    const hop3 = Array.isArray(careerPaths?.hop3) ? careerPaths.hop3 : [];
    const byOrg = new Map();
    [...hop2, ...hop3].forEach((item) => {
        if (!item || item.organization_id == null) return;
        const prev = byOrg.get(item.organization_id);
        if (!prev) {
            byOrg.set(item.organization_id, {
                organization_id: item.organization_id,
                organization: item.organization,
                count: Number(item.count || 0),
            });
            return;
        }
        prev.count += Number(item.count || 0);
    });
    return Array.from(byOrg.values()).sort((a, b) => (
        Number(b.count || 0) - Number(a.count || 0) ||
        String(a.organization || '').localeCompare(String(b.organization || ''))
    ));
};
/* ── MOCK DATA for Network Insights ── */
// Per-workplace tab data
const MOCK_WORK_TABS = {
    best: [
        { id: 1, name: 'Google', count: 8, tags: ['Training #6', 'Same tenure'] },
        { id: 2, name: 'Meesho', count: 4, tags: ['Same tenure'] },
        { id: 3, name: 'Flipkart', count: 3, tags: ['Industry'] },
    ],
    'Microsoft': [
        { id: 1, name: 'Google', count: 5, tags: ['Training #6'] },
        { id: 4, name: 'PhonePe', count: 2, tags: ['Same tenure'] },
    ],
    'Amazon': [
        { id: 2, name: 'Meesho', count: 4, tags: ['Same tenure'] },
    ],
};
// Per-college tab data
const MOCK_COLLEGE_TABS = {
    best: [
        { id: 1, name: 'Swiggy', count: 6, tags: ['IIT BOB', 'Metallurgy'] },
        { id: 2, name: 'Zepto', count: 3, tags: ['Same dept'] },
    ],
    'IIT Bombay': [
        { id: 1, name: 'Swiggy', count: 4, tags: ['Metallurgy'] },
        { id: 6, name: 'DeepMind', count: 1, tags: ['Dept match'] },
    ],
    'BITS Pilani': [
        { id: 2, name: 'Zepto', count: 3, tags: ['Same dept'] },
    ],
};
const MOCK_WORK_COMPANIES = [
    {
        id: 1,
        name: 'Google',
        tags: ['Training #6', 'Same tenure'],
        connectionCount: 8,
        mutualLabel: 'From both workplaces',
        workplaces: ['Workplace A', 'Workplace B', 'BrainPop'],
    },
    {
        id: 2,
        name: 'Meesho',
        tags: ['Classmates', 'Same tenure'],
        connectionCount: 4,
        mutualLabel: 'From both workplaces',
        workplaces: ['Workplace A', 'Workplace B'],
    },
];
const MOCK_COLLEGE_COMPANIES = [
    {
        id: 1,
        name: 'Swiggy',
        tags: ['IIT BOB', 'Metallurgy', 'Same tenure'],
        connectionCount: 6,
        mutualLabel: 'From both colleges',
        colleges: ['College A + IIT BOB'],
    },
];
/* ── NETWORK SECTION COMPONENTS ── */
const acol = (name = '') => {
    const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
    let h = 0; for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return cols[Math.abs(h) % cols.length];
};
const Pill = ({ label }) => {
    const { isDark } = useTheme();
    return (
        <span style={{
            padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
            background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
            color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
        }}>{label}</span>
    );
};
const MissingProfileBlock = ({ icon, color, label, missingText, onAdd }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '24px 12px', gap: 12, textAlign: 'center',
        background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)',
        borderRadius: 16, margin: '8px 0 12px'
    }}>
        <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: `${color}10`, border: `1px solid ${color}25`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: color
        }}>{icon}</div>
        <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, maxWidth: 280 }}>{missingText}</p>
        </div>
        <motion.button
            onClick={onAdd}
            initial={false}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
                padding: '8px 18px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                background: color, color: '#1C1917', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6
            }}
        >
            <PlusCircle size={13} /> Add your details
        </motion.button>
    </div>
);
const TabBar = ({ tabs, active, accent, onSelect }) => {
    const { isDark } = useTheme();
    return (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {tabs.map(tab => {
                const isActive = tab === active;
                return (
                    <button
                        key={tab}
                        onClick={() => onSelect(tab)}
                        title={tab.length > 20 ? tab : undefined}
                        style={{
                            padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                            background: isActive ? accent + '18' : (isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
                            border: `1px solid ${isActive ? accent + '40' : (isDark ? 'rgba(255,255,255,0.08)' : '#dbe6f7')}`,
                            color: isActive ? accent : (isDark ? 'rgba(255,255,255,0.5)' : '#64748b'),
                            cursor: 'pointer', transition: 'all 0.15s',
                        }}
                    >
                        {tab === 'best' ? 'Best Pick' : (tab.length > 20 ? tab.substring(0, 20) + '...' : tab)}
                    </button>
                );
            })}
        </div>
    );
};
const CompanyList = ({ rows, accent, onRowClick, relationField = 'strongest_entities' }) => {
    const { isDark } = useTheme();
    const txtMain = isDark ? 'rgba(255,255,255,0.9)' : '#1e293b';
    const txtMuted = isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8';
    const rowBg = isDark ? 'rgba(255,255,255,0.03)' : '#f8faff';
    const rowBdr = isDark ? 'rgba(255,255,255,0.06)' : '#e8edf8';
    const tagBg = isDark ? 'rgba(255,255,255,0.05)' : '#eef2fb';
    const tagBdr = isDark ? 'rgba(255,255,255,0.14)' : '#c7d7f5';
    const tagClr = isDark ? 'rgba(255,255,255,0.78)' : '#3b5ea8';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((c, i) => {
                const companyNameLabel = c.organization || c.name || 'Unknown';
                const col = acol(companyNameLabel);
                const relationRaw = c?.[relationField];
                const relationTags = Array.isArray(relationRaw)
                    ? relationRaw.filter(Boolean)
                    : (typeof relationRaw === 'string'
                        ? relationRaw.split('·').map((x) => x.trim()).filter(Boolean)
                        : []);
                const tags = relationTags.slice(0, 3);
                return (
                    <motion.div
                        key={`${companyNameLabel}-${i}`}
                        initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        onClick={onRowClick}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderRadius: 12,
                            background: rowBg, border: `1px solid ${rowBdr}`,
                            cursor: 'pointer', transition: 'border-color 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = accent + '50'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = rowBdr}
                    >
                        <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: col + '15', border: `1px solid ${col}25`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 800, color: col,
                        }}>{companyNameLabel[0]}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: txtMain, marginBottom: 2 }}>{companyNameLabel}</p>
                            {tags.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    {tags.map((tag, tIdx) => (
                                        <span
                                            key={`${companyNameLabel}-${tag}-${tIdx}`}
                                            title={tag.length > 20 ? tag : undefined}
                                            style={{
                                                padding: '2px 7px', borderRadius: 999,
                                                fontSize: 9, fontWeight: 700,
                                                color: tagClr, border: `1px solid ${tagBdr}`,
                                                background: tagBg,
                                            }}
                                        >
                                            {tag.length > 20 ? tag.substring(0, 20) + '...' : tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <p style={{ fontSize: 16, fontWeight: 900, color: accent, lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>{c.count}</p>
                            <p style={{ fontSize: 8, color: txtMuted, textTransform: 'uppercase', marginTop: 2 }}>contacts</p>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};
const BlockHeader = ({ color, icon, eyebrow, title, subtitle, stat }) => {
    const { isDark } = useTheme();
    const txtPrimary = isDark ? '#fff' : '#0f172a';
    const txtMuted = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    const statMuted = isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8';
    return (
        <div style={{
            background: `linear-gradient(135deg, ${color}0a, rgba(0,0,0,0))`,
            borderTop: `3px solid ${color}`,
            borderRadius: '0 0 16px 16px', padding: '22px 24px 18px',
            marginBottom: 20,
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: color + '18', border: `1px solid ${color}35`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>{icon}</div>
                        <span style={{ fontSize: 10, fontWeight: 800, color: color, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{eyebrow}</span>
                    </div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: txtPrimary, lineHeight: 1.25, marginBottom: 6, fontFamily: "'Playfair Display', serif" }}>{title}</h3>
                    <p style={{ fontSize: 12, color: txtMuted, lineHeight: 1.6, maxWidth: 560 }}>{subtitle}</p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 36, fontWeight: 900, color: color, lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>{stat}</p>
                    <p style={{ fontSize: 10, color: statMuted, marginTop: 4 }}>contacts</p>
                </div>
            </div>
        </div>
    );
};
const NetworkSection = React.forwardRef(({ companyName, goToList, crossCompanies = [], workTabsData = {}, profileWorkCompanies = [], collegeTabsData = {}, profileColleges = [], profileLoaded = false }, ref) => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [workTab, setWorkTab] = useState('best');
    const [collegeTab, setCollegeTab] = useState('best');
    const hasWork = (profileWorkCompanies?.length || 0) > 0;
    const hasCollege = (profileColleges?.length || 0) > 0;
    const backendWorkTabs = Object.keys(workTabsData || {}).filter(k => k !== 'best');
    const mergedWorkTabs = Array.from(new Set([...(Array.isArray(profileWorkCompanies) ? profileWorkCompanies : []), ...backendWorkTabs])).filter(Boolean);
    const workTabKeys = ['best', ...mergedWorkTabs];
    const backendCollegeTabs = Object.keys(collegeTabsData || {}).filter(k => k !== 'best');
    const mergedCollegeTabs = Array.from(new Set([...(Array.isArray(profileColleges) ? profileColleges : []), ...backendCollegeTabs])).filter(Boolean);
    const collegeTabKeys = ['best', ...mergedCollegeTabs];
    const hasMultipleWorkAnchors = mergedWorkTabs.length > 1;
    const hasMultipleCollegeAnchors = mergedCollegeTabs.length > 1;
    const workRows = Array.isArray(workTabsData?.[workTab]) ? workTabsData[workTab] : [];
    const colRows = Array.isArray(collegeTabsData?.[collegeTab]) ? collegeTabsData[collegeTab] : [];
    const isBestWorkInsufficient = workTab === 'best' && !hasMultipleWorkAnchors;
    const isBestCollegeInsufficient = collegeTab === 'best' && !hasMultipleCollegeAnchors;
    return (
        <React.Fragment>
            {/* Divider Header */}
            <div ref={ref} className="network-divider" style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 22, marginBottom: 18 }}>
                <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.16)' }} />
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 16px', borderRadius: 20,
                    background: isDark ? 'rgba(59,130,246,0.10)' : 'rgba(37,99,235,0.08)',
                    border: isDark ? '1px solid rgba(96,165,250,0.26)' : '1px solid rgba(37,99,235,0.20)',
                    transform: 'translateY(-4px)',
                }}>
                    <Network size={13} color={isDark ? '#60A5FA' : '#2563eb'} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#BFDBFE' : '#1d4ed8', letterSpacing: '0.01em' }}>
                        Companies where you can get referrals easily
                    </span>
                </div>
                <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.16)' }} />
            </div>
            {/* BLOCK 2: COLLEAGUES */}
            <Card style={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                    <CardTitle
                        color='#34D399'
                        icon={<Users size={18} color='#34D399' />}
                        eyebrow='ex-colleagues'
                        subtitle="People you worked with — now inside these companies. Pick your workplace to filter."
                    >
                        Professional Network
                    </CardTitle>
                    <div style={{ flex: 1 }}>
                        <TabBar tabs={workTabKeys} active={workTab} accent='#34D399' onSelect={setWorkTab} />
                        {!profileLoaded ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 10 }}>
                                <Loader2 size={16} className="animate-spin" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }} />
                            </div>
                        ) : !hasWork ? (
                            <MissingProfileBlock
                                color="#34D399"
                                icon={<Users size={20} />}
                                label="Colleague Map Locked"
                                missingText="Add your past work experiences to see who from your old teams are now working inside these companies."
                                onAdd={() => navigate('/profile')}
                            />
                        ) : isBestWorkInsufficient ? (
                            <MissingProfileBlock
                                color="#34D399"
                                icon={<Users size={20} />}
                                label="Best Pick needs multiple workplaces"
                                missingText="Add at least two work experiences in your profile to unlock Best Pick for work network."
                                onAdd={() => navigate('/profile')}
                            />
                        ) : workRows.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 16px', gap: 10, textAlign: 'center' }}>
                                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Users size={18} color="rgba(52,211,153,0.4)" />
                                </div>
                                <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.45)' : '#475569', margin: 0 }}>{workTab === 'best' ? 'No work-network data yet' : `No matches from ${workTab}`}</p>
                                <p style={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.3)' : '#64748b', margin: 0, maxWidth: 200, lineHeight: 1.6 }}>We haven't found companies where people share multiple work experience.</p>
                            </div>
                        ) : (
                            <CompanyList
                                rows={workRows.slice(0, 2)}
                                accent='#34D399'
                                onRowClick={() => goToList('work_network', { selectedWorkTab: workTab })}
                                relationField="strongest_relation"
                            />
                        )}
                    </div>
                    {hasWork && !isBestWorkInsufficient && workRows.length > 0 && (
                        <SeeMore color="#34D399" label={`SEE MORE (${workRows.length})`} onClick={() => goToList('work_network', { selectedWorkTab: workTab })} />
                    )}
                </CardContent>
            </Card>
            {/* BLOCK 3: CLASSMATES */}
            <Card style={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                    <CardTitle
                        color='#8B5CF6'
                        icon={<GraduationCap size={18} color='#8B5CF6' />}
                        eyebrow='ex-classmates'
                        subtitle="People you studied with — now inside these companies. Pick your college to filter."
                    >
                        Educational Network
                    </CardTitle>
                    <div style={{ flex: 1 }}>
                        <TabBar tabs={collegeTabKeys} active={collegeTab} accent='#8B5CF6' onSelect={setCollegeTab} />
                        {!profileLoaded ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 10 }}>
                                <Loader2 size={16} className="animate-spin" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }} />
                            </div>
                        ) : !hasCollege ? (
                            <MissingProfileBlock
                                color="#8B5CF6"
                                icon={<GraduationCap size={20} />}
                                label="Classmate Map Locked"
                                missingText="Add your education history to find classmates from your batch who are now working inside these companies."
                                onAdd={() => navigate('/profile')}
                            />
                        ) : isBestCollegeInsufficient ? (
                            <MissingProfileBlock
                                color="#8B5CF6"
                                icon={<GraduationCap size={20} />}
                                label="Best Pick needs multiple colleges"
                                missingText="Add at least two colleges in your profile to unlock Best Pick for education network."
                                onAdd={() => navigate('/profile')}
                            />
                        ) : colRows.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 16px', gap: 10, textAlign: 'center' }}>
                                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <GraduationCap size={18} color="rgba(139,92,246,0.4)" />
                                </div>
                                <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.45)' : '#475569', margin: 0 }}>{collegeTab === 'best' ? 'No education-network data yet' : `No matches from ${collegeTab}`}</p>
                                <p style={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.3)' : '#64748b', margin: 0, maxWidth: 200, lineHeight: 1.6 }}>We haven't found companies where people share multiple educational history.</p>
                            </div>
                        ) : (
                            <CompanyList
                                rows={colRows.slice(0, 2)}
                                accent='#8B5CF6'
                                onRowClick={() => goToList('education_network', { selectedCollegeTab: collegeTab })}
                                relationField="strongest_relation"
                            />
                        )}
                    </div>
                    {hasCollege && !isBestCollegeInsufficient && colRows.length > 0 && (
                        <SeeMore color="#8B5CF6" label={`SEE MORE (${colRows.length})`} onClick={() => goToList('education_network', { selectedCollegeTab: collegeTab })} />
                    )}
                </CardContent>
            </Card>
            {/* BLOCK 1: CROSS-NETWORK */}
            <Card style={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent>
                    <CardTitle
                        color='#F59E0B'
                        icon={<Zap size={18} color='#F59E0B' />}
                        eyebrow='Strongest pull'
                        subtitle="Companies where people share both your college and work experience."
                    >
                        Shared Work & College
                    </CardTitle>
                    <div style={{ flex: 1 }}>
                        {!profileLoaded ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 10 }}>
                                <Loader2 size={16} className="animate-spin" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }} />
                                <span style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b' }}>Checking network…</span>
                            </div>
                        ) : (!hasWork || !hasCollege) ? (
                            <MissingProfileBlock
                                color="#F59E0B"
                                icon={<Zap size={20} />}
                                label="Unlock Cross-Network Insights"
                                missingText="We need both your Work and Education history to find the companies where your network pulls from both sides."
                                onAdd={() => navigate('/profile')}
                            />
                        ) : crossCompanies.length > 0 ? (
                            <CompanyList
                                rows={crossCompanies.slice(0, 2)}
                                accent='#F59E0B'
                                onRowClick={() => goToList('cross_network')}
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 16px', gap: 12, textAlign: 'center' }}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Zap size={18} color="rgba(245,158,11,0.4)" />
                                </div>
                                <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.45)' : '#475569', margin: 0 }}>No cross-network matches yet</p>
                                <p style={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.3)' : '#64748b', margin: 0, maxWidth: 220, lineHeight: 1.6 }}>We found 0 companies where people share both your college and work experience.</p>
                            </div>
                        )}
                    </div>
                    {hasWork && hasCollege && crossCompanies.length > 0 && (
                        <SeeMore color="#F59E0B" label={`SEE MORE (${crossCompanies.length})`} onClick={() => goToList('cross_network')} />
                    )}
                </CardContent>
            </Card>
        </React.Fragment>
    );
});
/* ══════════════════════════════ MAIN SCREEN ══════════════════════════════ */
const CompanyInsights = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, openLogin, loading: authLoading } = useAuth();
    const { isDark } = useTheme();
    const appCls = isDark ? 'dark-app' : 'light-app';
    const accentColor = isDark ? '#F59E0B' : '#2563eb';
    const bgPage = isDark ? 'rgba(13,16,25,0.9)' : 'rgba(238,242,251,0.95)';
    const borderSub = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.12)';
    const nudgeTextColor = isDark ? 'rgba(255,255,255,0.78)' : '#64748b';
    const nudgeHeadingColor = isDark ? 'rgba(255,255,255,0.72)' : '#64748b';
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isProgressNudgeOpen, setIsProgressNudgeOpen] = useState(false);
    const [progressCounts, setProgressCounts] = useState({ apps: 0, referrals: 0 });
    const [showProgressNudgeCard, setShowProgressNudgeCard] = useState(true);
    const [isMobileLayout, setIsMobileLayout] = useState(() => getViewportWidth() <= 720);
    const profileMenuRef = useRef(null);
    /* read params from route state or session storage */
    const routeState = location.state || {};
    const savedState = (() => {
        if (!user) return {};
        try { return JSON.parse(sessionStorage.getItem(getStateKey(user)) || '{ }'); } catch { return {}; }
    })();
    const params = { ...savedState, ...routeState };
    const initialSourceData = useMemo(() => {
        const source = routeState?.sourceData;
        if (!source || typeof source !== 'object') return null;
        return {
            ...source,
            total_alumni: Number(source?.total_alumni ?? 0),
        };
    }, [routeState?.sourceData]);
    const onboardingNetworkMode = useMemo(() => {
        if (!params.autoNavigateToBlock) return null;
        if (params.selectedWorkTab) return 'work';
        if (params.selectedCollegeTab) return 'education';
        if (params.autoNavigateToBlock === 'cross_network') return 'cross';
        return null;
    }, [params.autoNavigateToBlock, params.selectedCollegeTab, params.selectedWorkTab]);
    const onboardingNetworkTab = params.selectedWorkTab || params.selectedCollegeTab || '';
    const onboardingNetworkTabId = params.selectedWorkTabId != null
        ? Number(params.selectedWorkTabId)
        : params.selectedCollegeTabId != null
            ? Number(params.selectedCollegeTabId)
            : undefined;
    const { orgId, companyName, startYear, endYear, orgNotFound, autoNavigateToBlock } = params;
    const [data, setData] = useState(initialSourceData);
    const [loading, setLoading] = useState(() => !initialSourceData);
    const [networkLoading, setNetworkLoading] = useState(false);
    const [networkFetchStatus, setNetworkFetchStatus] = useState(initialSourceData ? 'success' : 'idle');
    const [networkFetchError, setNetworkFetchError] = useState('');
    const [roleLoading, setRoleLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRole, setSelectedRole] = useState(params.selectedRole || '');
    const [selectedLocation, setSelectedLocation] = useState(params.selectedLocation || '');
    const [showRolePicker, setShowRolePicker] = useState(false);
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    const [showNetworkView, setShowNetworkView] = useState(params.showNetworkView || false);
    const [profile, setProfile] = useState(null);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [isReferralFabLoading, setIsReferralFabLoading] = useState(false);
    const [navTransition, setNavTransition] = useState(() => {
        if (!params.autoNavigateToBlock) return { active: false, title: null, subtitle: null, chip: null };
        const mode = params.autoNavigateToBlock;
        const wTab = params.selectedWorkTab || '';
        const cTab = params.selectedCollegeTab || '';
        const cName = params.companyName || companyName || '';
        let chip = null, title = null, subtitle = null;
        if (mode === 'work_network') {
            chip = 'Professional network';
            title = wTab && wTab !== 'best' ? `Companies where your ${wTab} network works now` : 'Professional Network';
            subtitle = `Filtering by ${wTab || 'best matches'}`;
        } else if (mode === 'education_network') {
            chip = 'Educational network';
            title = cTab && cTab !== 'best' ? `Companies where your ${cTab} alumni network works now` : 'Educational Network';
            subtitle = `Filtering by ${cTab || 'best matches'}`;
        } else if (mode === 'cross_network') {
            chip = 'Strongest pull';
            title = 'Where you have leverage from both sides';
            subtitle = `Matching companies where ${cName} alumni overlap with both backgrounds`;
        } else if (mode === 'active_hirers') {
            chip = 'Hiring right now';
            title = `Companies actively hiring from ${cName} right now`;
            subtitle = 'Matching companies where alumni moved recently';
        } else if (mode === 'next_destinations') {
            chip = 'Easy referral';
            title = `Where ${cName} people are actually landing in big numbers`;
            subtitle = 'Surfacing alumni-heavy destinations first';
        } else if (mode === 'career_paths') {
            chip = 'Career paths';
            title = `Where ${cName} alumni go after their first move`;
            subtitle = 'Building likely next-step companies';
        } else if (mode === 'best_tenure') {
            chip = 'Stable workplace';
            title = `Companies where ${cName} alumni stayed the longest`;
            subtitle = 'Prioritizing places where people stayed longer';
        } else if (mode === 'role_hiring') {
            const r = params.selectedRole || 'your role';
            chip = r; title = `Top companies hiring ${r} from ${cName}`; subtitle = `Matching companies that hired ${r} from ${cName}`;
        } else if (mode === 'location_hiring') {
            const l = params.selectedLocation || 'your location';
            chip = l; title = `Where ${cName} alumni are getting hired in ${l}`; subtitle = `Matching companies active in ${l}`;
        }
        return { active: true, title, subtitle, chip };
    });
    const navTransitionTimerRef = useRef(null);
    const [dismissedNewTags, setDismissedNewTags] = useState(() => readDismissedNewTags(user?.id));

    useEffect(() => {
        const update = () => setIsMobileLayout(getViewportWidth() <= 720);
        update();
        window.addEventListener('resize', update);
        window.visualViewport?.addEventListener('resize', update);
        return () => {
            window.removeEventListener('resize', update);
            window.visualViewport?.removeEventListener('resize', update);
        };
    }, []);
    const [pendingReferralScroll, setPendingReferralScroll] = useState(false);
    const [isExportingAggregated, setIsExportingAggregated] = useState(false);
    const hasAutoEnhancedRef = useRef(false);
    const autoNavBlockHandledRef = useRef(false);
    const roleTouchedByUserRef = useRef(!!params.selectedRole);
    const locationTouchedByUserRef = useRef(!!params.selectedLocation);
    const skipInitialRoleFetchRef = useRef(Boolean(initialSourceData && params.selectedRole) || Boolean(onboardingNetworkMode));
    const skipInitialLocationFetchRef = useRef(Boolean(initialSourceData && params.selectedLocation) || Boolean(onboardingNetworkMode));
    useEffect(() => {
        setDismissedNewTags(readDismissedNewTags(user?.id));
    }, [user?.id]);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const userKey = String(user?.id || user?.email || 'anon');
        const doneKey = getProgressFirstSessionDoneKey(userKey);
        const startKey = getProgressFirstSessionStartKey(userKey);
        try {
            const done = localStorage.getItem(doneKey) === '1';
            if (done) {
                setShowProgressNudgeCard(true);
                return;
            }
            setShowProgressNudgeCard(false);
            const now = Date.now();
            const existingStart = Number(localStorage.getItem(startKey));
            const startTs = Number.isFinite(existingStart) && existingStart > 0 ? existingStart : now;
            if (!existingStart || !Number.isFinite(existingStart)) {
                localStorage.setItem(startKey, String(startTs));
            }
            const elapsed = now - startTs;
            const remaining = Math.max(0, FIRST_SESSION_NUDGE_MS - elapsed);
            const timer = window.setTimeout(() => {
                setShowProgressNudgeCard(true);
                localStorage.setItem(doneKey, '1');
            }, remaining);
            return () => window.clearTimeout(timer);
        } catch {
            setShowProgressNudgeCard(true);
        }
    }, [user?.id, user?.email]);
    // Persist showNetworkView for this company
    useEffect(() => {
        if (!user || !orgId) return;
        const key = getStateKey(user);
        try {
            const saved = JSON.parse(sessionStorage.getItem(key) || '{}');
            if (saved.showNetworkView !== showNetworkView) {
                sessionStorage.setItem(key, JSON.stringify({ ...saved, showNetworkView }));
            }
        } catch (e) { }
    }, [showNetworkView, user, orgId]);
    const networkSectionRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const referralClickTsRef = useRef(0);
    const networkCaptureForClickDoneRef = useRef(false);
    const referralFallbackTimerRef = useRef(null);
    const startDate = startYear ? `${startYear}-01-01` : null;
    const endDate = endYear ? `${endYear}-12-31` : null;
    const fullReqSeqRef = useRef(0);
    const roleReqSeqRef = useRef(0);
    const locationReqSeqRef = useRef(0);
    const inFlightRequestKeysRef = useRef(new Set());

    const retryNetworkFetch = useCallback(async () => {
        if (!orgId) return;
        setNetworkLoading(true);
        setNetworkFetchStatus('loading');
        setNetworkFetchError('');
        try {
            const networkResult = await getCompanyInsights(orgId, {
                startDate,
                endDate,
                role: selectedRole,
                includeCore: false,
                includeNetwork: true,
                includeRole: false,
                includeLocation: false,
                timeoutMs: 240000,
            });
            setData(prev => {
                const prevData = (prev && typeof prev === 'object') ? prev : {};
                return {
                    ...prevData,
                    ...(networkResult || {}),
                    total_alumni: Number((prevData?.total_alumni ?? networkResult?.total_alumni ?? 0)),
                };
            });
            setNetworkFetchStatus('success');
            setNetworkFetchError('');
        } catch (networkErr) {
            setNetworkFetchStatus('error');
            setNetworkFetchError(networkErr?.message || 'Network overlap blocks are slow right now. Please try again.');
        } finally {
            setNetworkLoading(false);
        }
    }, [orgId, startDate, endDate, selectedRole]);

    const fetchInsights = useCallback(async (role = '', location = '', { partialRoleUpdate = false, partialLocationUpdate = false } = {}) => {
        if (!orgId) return;
        const requestKind = partialRoleUpdate ? 'role' : (partialLocationUpdate ? 'location' : 'full');
        const effectiveRole = partialLocationUpdate ? '' : (role || '');
        const effectiveLocation = partialRoleUpdate ? '' : (location || '');
        const dedupeKey = [orgId, startDate || '', endDate || '', requestKind, effectiveRole, effectiveLocation].join('|');
        if (inFlightRequestKeysRef.current.has(dedupeKey)) return;
        inFlightRequestKeysRef.current.add(dedupeKey);
        const requestSeq = partialRoleUpdate
            ? ++roleReqSeqRef.current
            : partialLocationUpdate
                ? ++locationReqSeqRef.current
                : ++fullReqSeqRef.current;
        const isStale = () => {
            if (partialRoleUpdate) return requestSeq !== roleReqSeqRef.current;
            if (partialLocationUpdate) return requestSeq !== locationReqSeqRef.current;
            return requestSeq !== fullReqSeqRef.current;
        };
        if (partialRoleUpdate) {
            setRoleLoading(true);
        } else if (partialLocationUpdate) {
            setLocationLoading(true);
        } else {
            // Check cache for full load
            const cacheParams = { startDate, endDate, role: effectiveRole, location: effectiveLocation };
            const cached = getCachedInsights(orgId, cacheParams);
            if (cached) {
                setData(cached);
                setLoading(false);
                setNetworkLoading(false);
                setNetworkFetchStatus('success');
                setNetworkFetchError('');
                // Still refresh in background? For now, just return to satisfy user "speed"
                inFlightRequestKeysRef.current.delete(dedupeKey);
                return;
            }
            setLoading(true);
            setNetworkLoading(false);
            setNetworkFetchStatus('idle');
            setNetworkFetchError('');
            setError(null);
        }
        try {
            if (partialRoleUpdate) {
                const cacheParams = { startDate, endDate, role: effectiveRole, type: 'role' };
                const cached = getCachedInsights(orgId, cacheParams);
                if (cached) {
                    setData(prev => ({ ...prev, ...cached }));
                    setRoleLoading(false);
                    return;
                }
                const result = await getCompanyInsights(orgId, {
                    startDate,
                    endDate,
                    role: effectiveRole,
                    location: '',
                    includeCore: false,
                    includeNetwork: false,
                    includeRole: true,
                    includeLocation: false,
                });
                if (isStale()) return;
                const update = {
                    role_hiring: Array.isArray(result?.role_hiring) ? result.role_hiring : [],
                    selected_role: result?.selected_role ?? null,
                    available_roles: Array.isArray(result?.available_roles) ? result.available_roles : [],
                };
                setData(prev => {
                    if (!prev) return result;
                    return { ...prev, ...update };
                });
                setCachedInsights(orgId, cacheParams, update);
            } else if (partialLocationUpdate) {
                const cacheParams = { startDate, endDate, location: effectiveLocation, type: 'location' };
                const cached = getCachedInsights(orgId, cacheParams);
                if (cached) {
                    setData(prev => ({ ...prev, ...cached }));
                    setLocationLoading(false);
                    return;
                }
                const result = await getCompanyInsights(orgId, {
                    startDate,
                    endDate,
                    role: '',
                    location: effectiveLocation,
                    includeCore: false,
                    includeNetwork: false,
                    includeRole: false,
                    includeLocation: true,
                });
                if (isStale()) return;
                const update = {
                    location_hiring: Array.isArray(result?.location_hiring) ? result.location_hiring : [],
                    selected_location: result?.selected_location ?? null,
                    available_locations: Array.isArray(result?.available_locations) ? result.available_locations : [],
                };
                setData(prev => {
                    if (!prev) return result;
                    return { ...prev, ...update };
                });
                setCachedInsights(orgId, cacheParams, update);
            } else {
                const coreResult = await getCompanyInsights(orgId, {
                    startDate,
                    endDate,
                    role: effectiveRole,
                    location: effectiveLocation,
                    includeCore: true,
                    includeNetwork: false,
                    includeRole: true,
                    includeLocation: true,
                });
                if (isStale()) return;
                const baseData = {
                    ...(coreResult || {}),
                    total_alumni: Number(coreResult?.total_alumni ?? 0),
                };
                setData(baseData);
                setLoading(false);
                setNetworkLoading(true);
                setNetworkFetchStatus('loading');
                setNetworkFetchError('');
                try {
                    const networkResult = await getCompanyInsights(orgId, {
                        startDate,
                        endDate,
                        role: effectiveRole,
                        includeCore: false,
                        includeNetwork: true,
                        includeRole: false,
                        includeLocation: false,
                        timeoutMs: 240000,
                    });
                    if (isStale()) return;
                    setData(prev => {
                        const prevData = (prev && typeof prev === 'object') ? prev : (baseData || {});
                        return {
                            ...prevData,
                            ...(networkResult || {}),
                            total_alumni: Number((prevData?.total_alumni ?? baseData?.total_alumni ?? networkResult?.total_alumni ?? 0)),
                        };
                    });
                    setNetworkFetchStatus('success');
                    setNetworkFetchError('');
                    // Cache the full result
                    setCachedInsights(orgId, {
                        startDate,
                        endDate,
                        role: effectiveRole,
                        location: effectiveLocation,
                    }, {
                        ...(baseData || {}),
                        ...(networkResult || {}),
                        total_alumni: Number((baseData?.total_alumni ?? networkResult?.total_alumni ?? 0)),
                    });
                } catch (networkErr) {
                    if (isStale()) return;
                    setNetworkFetchStatus('error');
                    setNetworkFetchError(networkErr?.message || 'Network overlap blocks are slow right now. Please try again.');
                } finally {
                    if (isStale()) return;
                    setNetworkLoading(false);
                }
            }
        } catch (err) {
            if (isStale()) return;
            if (partialRoleUpdate) {
                toast.error(err.message || 'Failed to refresh role hiring');
            } else if (partialLocationUpdate) {
                toast.error(err.message || 'Failed to refresh location hiring');
            } else {
                setError(err.message || 'Failed to load insights');
                setNetworkFetchStatus('error');
                setNetworkFetchError(err?.message || 'Failed to load network insights');
            }
        } finally {
            inFlightRequestKeysRef.current.delete(dedupeKey);
            if (isStale()) return;
            if (partialRoleUpdate) {
                setRoleLoading(false);
            } else if (partialLocationUpdate) {
                setLocationLoading(false);
            } else {
                setLoading(false);
                setNetworkLoading(false);
            }
        }
    }, [orgId, startDate, endDate]);
    useEffect(() => {
        if (authLoading) return;
        if (!user) { openLogin(); return; }
        if (!orgId) {
            if (orgNotFound) {
                // go to dashboard for "not found" flow
                navigate('/dashboard', { state: params, replace: true });
                return;
            }
            navigate('/', { replace: true });
            return;
        }
        if (initialSourceData) {
            setData(initialSourceData);
            setLoading(false);
            setNetworkLoading(false);
            setNetworkFetchStatus('success');
            setNetworkFetchError('');
            setProfileLoaded(false);
            getProfile()
                .then(p => {
                    setProfile(p);
                    setProfileLoaded(true);
                })
                .catch(() => {
                    setProfile(null);
                    setProfileLoaded(true);
                });
            return;
        }
        if (onboardingNetworkMode) {
            const targetedFetch = async () => {
                try {
                    setLoading(true);
                    setNetworkLoading(true);
                    setNetworkFetchStatus('loading');
                    setNetworkFetchError('');
                    setError(null);
                    const result = await getCompanyInsights(orgId, {
                        startDate,
                        endDate,
                        includeCore: false,
                        includeNetwork: true,
                        includeRole: false,
                        includeLocation: false,
                        networkMode: onboardingNetworkMode,
                        networkTab: onboardingNetworkTab,
                        networkTabId: onboardingNetworkTabId,
                        timeoutMs: 240000,
                    });
                    const normalized = {
                        ...(result || {}),
                        total_alumni: Number(result?.total_alumni ?? 0),
                    };
                    setData(normalized);
                    setLoading(false);
                    setNetworkLoading(false);
                    setNetworkFetchStatus('success');
                    setNetworkFetchError('');
                } catch (err) {
                    setError(err?.message || 'Failed to load insights');
                    setNetworkFetchStatus('error');
                    setNetworkFetchError(err?.message || 'Failed to load network insights');
                    setLoading(false);
                    setNetworkLoading(false);
                }
            };
            targetedFetch();
            setProfileLoaded(false);
            getProfile()
                .then(p => {
                    setProfile(p);
                    setProfileLoaded(true);
                })
                .catch(() => {
                    setProfile(null);
                    setProfileLoaded(true);
                });
            return;
        }
        fetchInsights(selectedRole, selectedLocation);
        // Fetch profile in parallel for the 6th card network check
        setProfileLoaded(false);
        getProfile()
            .then(p => {
                setProfile(p);
                setProfileLoaded(true);
            })
            .catch(() => {
                setProfile(null);
                setProfileLoaded(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user, orgId, orgNotFound, onboardingNetworkMode, onboardingNetworkTab, onboardingNetworkTabId, startDate, endDate]);
    useEffect(() => {
        if (!authLoading && user && orgId && data !== null) {
            if (skipInitialRoleFetchRef.current) {
                skipInitialRoleFetchRef.current = false;
                return;
            }
            fetchInsights(selectedRole, '', { partialRoleUpdate: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole]);
    useEffect(() => {
        if (!authLoading && user && orgId && data !== null) {
            if (skipInitialLocationFetchRef.current) {
                skipInitialLocationFetchRef.current = false;
                return;
            }
            fetchInsights('', selectedLocation, { partialLocationUpdate: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation]);
    // One-time delayed auto-enhancement:
    // 1) show generic cards first (fast)
    // 2) then auto-apply role/location once in the background
    useEffect(() => {
        if (!data || loading || error) return;
        if (hasAutoEnhancedRef.current) return;
        hasAutoEnhancedRef.current = true;
        const autoRole =
            !selectedRole &&
                !roleTouchedByUserRef.current &&
                Array.isArray(data.available_roles) &&
                data.available_roles.length > 0
                ? data.available_roles[0]
                : '';
        const autoLocation =
            !selectedLocation &&
                !locationTouchedByUserRef.current &&
                Array.isArray(data.available_locations) &&
                data.available_locations.length > 0
                ? data.available_locations[0]
                : '';
        if (!autoRole && !autoLocation) return;
        let t2 = null;
        const t1 = setTimeout(() => {
            if (autoRole && !roleTouchedByUserRef.current && !selectedRole) {
                setSelectedRole(autoRole);
            }
            t2 = setTimeout(() => {
                if (autoLocation && !locationTouchedByUserRef.current && !selectedLocation) {
                    setSelectedLocation(autoLocation);
                }
            }, 350);
        }, 700);
        return () => {
            clearTimeout(t1);
            if (t2) clearTimeout(t2);
        };
    }, [data, loading, error, selectedRole, selectedLocation]);
    // Auto-navigate to a specific block when coming from onboarding.
    // Navigate immediately when data is ready, or force-navigate after 1.7s max.
    useEffect(() => {
        if (!autoNavigateToBlock || autoNavBlockHandledRef.current) return;

        const doNavigate = (forced) => {
            if (autoNavBlockHandledRef.current) return;
            autoNavBlockHandledRef.current = true;
            const mode = autoNavigateToBlock;
            const extra = {};
            if (params.selectedRole) extra.selectedRole = params.selectedRole;
            if (params.selectedLocation) extra.selectedLocation = params.selectedLocation;
            if (params.selectedWorkTab) extra.selectedWorkTab = params.selectedWorkTab;
            if (params.selectedCollegeTab) extra.selectedCollegeTab = params.selectedCollegeTab;
            goToList(mode, { ...extra, replace: true, fromOnboarding: true, skipTransitionDelay: !!forced });
        };

        // If data is already available, navigate right away
        if (!loading && !error && data) {
            doNavigate(false);
            return;
        }

        // Otherwise, force-navigate after 1.7s even if data hasn't loaded
        const timeout = setTimeout(() => doNavigate(true), 1700);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error, data, autoNavigateToBlock]);
    /* ── close profile menu clicking outside ── */
    useEffect(() => {
        const fn = (e) => { if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setIsProfileMenuOpen(false); };
        document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, []);
    useEffect(() => {
        if (!showNetworkView) return;
        const t = setTimeout(() => {
            if (networkSectionRef.current) {
                const yOffset = -100; // Account for sticky header + some breathing room
                const element = networkSectionRef.current;
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        }, 100);
        return () => clearTimeout(t);
    }, [showNetworkView]);
    /* ── navigate to full dashboard ── */
    const goToDashboard = () => {
        navigate('/dashboard', { state: params });
    };
    const getTransitionCopy = useCallback((mode, extra = {}) => {
        const roleLabel = String(extra?.selectedRole || selectedRole || 'your role').trim();
        const locationLabel = String(extra?.selectedLocation || selectedLocation || 'your location').trim();
        switch (mode) {
            case 'cross_network':
                return {
                    chip: 'Strongest pull',
                    title: 'Where you have leverage from both sides',
                    subtitle: `Matching companies where ${companyName} alumni overlap with both backgrounds`,
                };
            case 'work_network':
                return {
                    chip: 'Professional network',
                    title: extra?.selectedWorkTab && extra.selectedWorkTab !== 'best'
                        ? `Companies where your ${extra.selectedWorkTab} network works now`
                        : 'Professional Network',
                    subtitle: `Filtering by ${String(extra?.selectedWorkTab || 'best matches')}`,
                };
            case 'education_network':
                return {
                    chip: 'Educational network',
                    title: extra?.selectedCollegeTab && extra.selectedCollegeTab !== 'best'
                        ? `Companies where your ${extra.selectedCollegeTab} alumni network works now`
                        : 'Educational Network',
                    subtitle: `Filtering by ${String(extra?.selectedCollegeTab || 'best matches')}`,
                };
            case 'active_hirers':
                return {
                    chip: 'Hiring right now',
                    title: `Companies actively hiring from ${companyName} right now`,
                    subtitle: 'Matching companies where alumni moved recently',
                };
            case 'next_destinations':
                return {
                    chip: 'Easy referral',
                    title: `Where ${companyName} people are actually landing in big numbers`,
                    subtitle: 'Surfacing alumni-heavy destinations first',
                };
            case 'career_paths':
                return {
                    chip: 'Career paths',
                    title: `Where ${companyName} alumni go after their first move`,
                    subtitle: 'Building likely next-step companies',
                };
            case 'best_tenure':
                return {
                    chip: 'Stable workplace',
                    title: `Companies where ${companyName} alumni stayed the longest`,
                    subtitle: 'Prioritizing places where people stayed longer',
                };
            case 'role_hiring':
                return {
                    chip: roleLabel,
                    title: roleLabel
                        ? `Top companies hiring ${roleLabel} from ${companyName}`
                        : `Top companies hiring from ${companyName}`,
                    subtitle: `Matching companies that hired ${roleLabel} from ${companyName}`,
                };
            case 'location_hiring':
                return {
                    chip: locationLabel,
                    title: locationLabel
                        ? `Where ${companyName} alumni are getting hired in ${locationLabel}`
                        : `Where ${companyName} alumni are getting hired`,
                    subtitle: `Matching companies active in ${locationLabel}`,
                };
            default:
                return {
                    chip: null,
                    title: null,
                    subtitle: null,
                };
        }
    }, [companyName, selectedLocation, selectedRole]);
    const startListTransition = useCallback((mode, destinationState, extra = {}) => {
        if (navTransitionTimerRef.current) {
            clearTimeout(navTransitionTimerRef.current);
            navTransitionTimerRef.current = null;
        }
        const copy = getTransitionCopy(mode, extra);
        setNavTransition({
            active: true,
            title: copy.title,
            subtitle: copy.subtitle,
            chip: copy.chip,
        });
        const transitionDelayMs = extra?.skipTransitionDelay ? 0 : extra?.fromOnboarding ? 600 : 1700;
        navTransitionTimerRef.current = setTimeout(() => {
            navigate('/insights-list', { state: destinationState, replace: extra.replace || false });
            navTransitionTimerRef.current = null;
        }, transitionDelayMs);
    }, [getTransitionCopy, navigate]);
    const handleAggregatedExport = useCallback(async () => {
        if (isExportingAggregated) return;
        setIsExportingAggregated(true);
        try {
            const data = await exportAggregatedJobMatches({
                startDate,
                endDate,
                role: selectedRole || undefined,
            });
            toast.success(
                `Export ready: ${data?.total_matching_jobs ?? 0} matched jobs across ${data?.total_companies_with_job_matches ?? 0} companies.`
            );
        } catch (err) {
            toast.error(err?.message || 'Failed to generate export');
        } finally {
            setIsExportingAggregated(false);
        }
    }, [endDate, isExportingAggregated, selectedRole, startDate]);
    /* ── navigate to the company list drilldown ── */
    const goToList = (mode, extra = {}) => {
        const { replace = false, ...otherExtra } = extra;
        const destinationState = {
            orgId, companyName, startYear, endYear,
            mode,
            ...otherExtra,
            showNetworkView,
            sourceData: data,
            fromInsightsTransition: true,
        };
        startListTransition(mode, destinationState, { replace, ...otherExtra });
    };
    const goToCompanyDetailsFromCross = (company) => {
        if (!company?.organization_id) return;
        navigate('/company-details', {
            state: {
                sourceOrgId: orgId,
                sourceCompanyName: companyName,
                destOrgId: company.organization_id,
                companyName: company.organization,
                hop: 1,
                startYear,
                endYear,
                role: '',
                initialPeopleTab: 'transition',
                crossMatchedEmployeeIds: Array.isArray(company?.matched_employee_ids) ? company.matched_employee_ids : [],
            }
        });
    };
    const handleRoleSelect = (role) => {
        roleTouchedByUserRef.current = true;
        setSelectedRole(role);
        setShowRolePicker(false);
    };
    const handleLocationSelect = (loc) => {
        locationTouchedByUserRef.current = true;
        setSelectedLocation(loc);
        setShowLocationPicker(false);
    };
    const alumniCount = data?.total_alumni ?? 0;
    const APP_AVG = 11;
    const REF_AVG = 8;
    useEffect(() => {
        if (!user || !orgId) return;
        setProgressCounts(readInsightsProgress(user?.id || user?.email, orgId));
    }, [user, orgId]);
    useEffect(() => {
        if (!user || !orgId) return;
        const id = setInterval(() => {
            setProgressCounts(readInsightsProgress(user?.id || user?.email, orgId));
        }, 1200);
        return () => clearInterval(id);
    }, [user, orgId]);
    useEffect(() => {
        return () => {
            if (navTransitionTimerRef.current) {
                clearTimeout(navTransitionTimerRef.current);
                navTransitionTimerRef.current = null;
            }
        };
    }, []);
    const progressNudge = useMemo(
        () => getProgressNudgeState(progressCounts.apps, progressCounts.referrals, APP_AVG, REF_AVG),
        [progressCounts.apps, progressCounts.referrals]
    );
    const progressItems = useMemo(() => ([
        { label: 'Apps', value: progressCounts.apps, avg: APP_AVG },
        { label: 'Referrals', value: progressCounts.referrals, avg: REF_AVG },
    ]), [progressCounts.apps, progressCounts.referrals]);
    const progressCompactSuffix = useMemo(() => {
        if (progressNudge.tone === 'caught_up') return "a little more and you're there";
        if (progressNudge.tone === 'halfway') return "almost there";
        return "others with your profile are already ahead";
    }, [progressNudge.tone]);
    const rolePlural = useMemo(() => {
        const r = selectedRole || 'Product';
        const low = r.toLowerCase();
        if (low === 'product' || low === 'pm') return 'PMs';
        if (low === 'engineering') return 'Engineers';
        if (low === 'design') return 'Designers';
        if (r.endsWith('s')) return r;
        if (r.endsWith('y')) return r.slice(0, -1) + 'ies';
        return r + 's';
    }, [selectedRole]);
    const activeHirers = Array.isArray(data?.active_hirers) ? data.active_hirers : [];
    const nextDestinations = Array.isArray(data?.next_destinations) ? data.next_destinations : [];
    const bestTenure = Array.isArray(data?.best_tenure) ? data.best_tenure : [];
    const roleHiringRows = Array.isArray(data?.role_hiring) ? data.role_hiring : [];
    const locationHiringRows = Array.isArray(data?.location_hiring) ? data.location_hiring : [];
    const mergedCareerPaths = mergeCareerPathList(data?.career_paths);
    const profileWorkCompanies = useMemo(() => {
        const root = (profile && typeof profile === 'object' && profile.profile && typeof profile.profile === 'object') ? profile.profile : (profile || {});
        const list = Array.isArray(root.work_experiences) ? root.work_experiences : [];
        const seen = new Set();
        const out = [];
        for (const w of list) {
            const name = String(w?.company || w?.organization || '').trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(name);
        }
        return out;
    }, [profile]);
    const profileWorkMatchedOrgIdByName = useMemo(() => {
        const root = (profile && typeof profile === 'object' && profile.profile && typeof profile.profile === 'object') ? profile.profile : (profile || {});
        const list = Array.isArray(root.work_experiences) ? root.work_experiences : [];
        const out = {};
        for (const w of list) {
            const name = String(w?.company || w?.organization || '').trim();
            const matchedId = Number(w?.org_id);
            if (!name || !Number.isFinite(matchedId)) continue;
            out[name.toLowerCase()] = matchedId;
        }
        return out;
    }, [profile]);
    const profileColleges = useMemo(() => {
        const root = (profile && typeof profile === 'object' && profile.profile && typeof profile.profile === 'object') ? profile.profile : (profile || {});
        const list = Array.isArray(root.educations) ? root.educations : [];
        const seen = new Set();
        const out = [];
        for (const e of list) {
            const name = String(e?.college || e?.school || e?.institution || '').trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(name);
        }
        return out;
    }, [profile]);
    const profileSchoolMatchedIdByName = useMemo(() => {
        const root = (profile && typeof profile === 'object' && profile.profile && typeof profile.profile === 'object') ? profile.profile : (profile || {});
        const list = Array.isArray(root.educations) ? root.educations : [];
        const out = {};
        for (const e of list) {
            const name = String(e?.college || e?.school || e?.institution || '').trim();
            const matchedId = Number(e?.school_id);
            if (!name || !Number.isFinite(matchedId)) continue;
            out[name.toLowerCase()] = matchedId;
        }
        return out;
    }, [profile]);
    const profileLength = (profileWorkCompanies?.length || 0) + (profileColleges?.length || 0);
    const hasAttachedProfile = Boolean(profile && profileLength > 0);
    const scrollToBottomAnchor = useCallback((instant = false) => {
        if (bottomAnchorRef.current) {
            bottomAnchorRef.current.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'end' });
            return;
        }
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: instant ? 'auto' : 'smooth'
        });
    }, []);
    const captureNetworkBlocksForSource = useCallback(async () => {
        if (!orgId || !data) {
            console.log('[NetworkCapture] ❌ Skipping: Missing orgId or data');
            return;
        }
        if (!data?.source_org_in_profile) {
            console.log(`[NetworkCapture] ❌ Skipping: source_org_in_profile is false for orgId=${orgId}`);
            return;
        }
        if (networkCaptureForClickDoneRef.current) return;
        const grouped = {};
        const pushUnique = (key, mapType, referenceName, referenceId, reasonType, sourceOrgId, destinationOrgId) => {
            const sid = Number(sourceOrgId);
            const did = Number(destinationOrgId);
            if (!Number.isFinite(sid) || !Number.isFinite(did)) return;
            if (!reasonType) return;
            grouped[key] = grouped[key] || {
                mapType,
                referenceName,
                referenceId,
                reasonType,
                sourceOrgId: sid,
                destinationSet: new Set()
            };
            grouped[key].destinationSet.add(did);
        };
        const inProfile = !!data?.source_org_in_profile;
        console.log(`[NetworkCapture] ▶ Starting capture for orgId=${orgId} (inProfile=${inProfile})`);
        const workTabs = (data?.network_work_tabs && typeof data.network_work_tabs === 'object') ? data.network_work_tabs : {};
        const workTabRefIds = (data?.network_work_tab_ref_ids && typeof data.network_work_tab_ref_ids === 'object') ? data.network_work_tab_ref_ids : {};

        const workTabEntries = Object.entries(workTabs);
        console.log(`[NetworkCapture] Found ${workTabEntries.length} work tabs`);
        workTabEntries.forEach(([tabName, rows]) => {
            if (String(tabName || '').toLowerCase() === 'best') return;
            const tabOrgId = Number(workTabRefIds[String(tabName)] ?? profileWorkMatchedOrgIdByName[String(tabName || '').toLowerCase()]);
            if (!Number.isFinite(tabOrgId)) {
                console.log(`[NetworkCapture] ⚠️ Missing ID for work tab: ${tabName}`);
                return;
            }
            const reason = `people from ${tabName} working here`;
            (Array.isArray(rows) ? rows : []).forEach((row) => {
                pushUnique(`work:${tabName}`, 'work', tabName, tabOrgId, reason, orgId, row?.organization_id);
            });
        });
        const eduTabs = (data?.network_education_tabs && typeof data.network_education_tabs === 'object') ? data.network_education_tabs : {};
        const eduTabRefIds = (data?.network_education_tab_ref_ids && typeof data.network_education_tab_ref_ids === 'object') ? data.network_education_tab_ref_ids : {};

        const eduTabEntries = Object.entries(eduTabs);
        console.log(`[NetworkCapture] Found ${eduTabEntries.length} education tabs`);
        eduTabEntries.forEach(([tabName, rows]) => {
            if (String(tabName || '').toLowerCase() === 'best') return;
            const schoolId = Number(eduTabRefIds[String(tabName)] ?? profileSchoolMatchedIdByName[String(tabName || '').toLowerCase()]);
            if (!Number.isFinite(schoolId)) {
                console.log(`[NetworkCapture] ⚠️ Missing ID for education tab: ${tabName}`);
                return;
            }
            const reason = `people from ${tabName} working here`;
            (Array.isArray(rows) ? rows : []).forEach((row) => {
                pushUnique(`education:${tabName}`, 'education', tabName, schoolId, reason, orgId, row?.organization_id);
            });
        });
        const buckets = Object.values(grouped);
        if (buckets.length === 0) {
            console.log('[NetworkCapture] ℹ️ No mappings to capture (tabs might be empty)');
            return;
        }

        console.log(`[NetworkCapture] 🚀 Prechecking ${buckets.length} mapping groups with backend...`);
        networkCaptureForClickDoneRef.current = true;

        let missingBlocks = [];
        try {
            const precheckRes = await precheckOrgDestinationMapCapture({
                sourceOrgId: orgId,
                blocks: buckets.map((b) => ({
                    key: b.key || `${b.mapType}:${b.referenceName}`,
                    map_type: b.mapType,
                    reason_type: b.reasonType,
                    reference_name: b.referenceName,
                    reference_id: b.referenceId,
                })),
            });
            missingBlocks = Array.isArray(precheckRes?.missing) ? precheckRes.missing : [];
            console.log(`[NetworkCapture] Precheck completed. Total groups: ${buckets.length}, Missing/To-Capture: ${missingBlocks.length}`);
        } catch (precheckErr) {
            console.error('[NetworkCapture] ⚠️ Precheck failed, falling back to full capture:', precheckErr);
            // Fallback: assume all buckets need capture if precheck fails
            missingBlocks = buckets.map((b) => ({ key: b.key || `${b.mapType}:${b.referenceName}` }));
        }

        const missingKeys = new Set(missingBlocks.map((mb) => mb.key));
        const bucketsToCapture = buckets.filter((b) => {
            const key = b.key || `${b.mapType}:${b.referenceName}`;
            return missingKeys.has(key);
        });

        if (bucketsToCapture.length === 0) {
            console.log('[NetworkCapture] 🎉 All mapping groups already captured in a previous run. Skipping capture API requests!');
            return;
        }

        console.log(`[NetworkCapture] 🚀 Sending ${bucketsToCapture.length} mapping groups to backend...`);
        await Promise.all(
            bucketsToCapture.map(async (bucket) => {
                const destinationOrgIds = Array.from(bucket.destinationSet || []);
                if (!destinationOrgIds.length) return;
                try {
                    await captureOrgDestinationMap({
                        sourceOrgId: bucket.sourceOrgId,
                        reasonType: bucket.reasonType,
                        mapType: bucket.mapType,
                        referenceName: bucket.referenceName,
                        referenceId: bucket.referenceId,
                        destinationOrgIds,
                    });
                    console.log(`[NetworkCapture] ✅ Successfully captured map for ${bucket.referenceName}`);
                } catch (err) {
                    console.error(`[NetworkCapture] ❌ Failed to capture map for ${bucket.referenceName}:`, err);
                }
            })
        );
    }, [data, orgId, profileWorkMatchedOrgIdByName, profileSchoolMatchedIdByName]);
    const handleFindReferralsClick = useCallback(() => {
        if (profileLoaded && !hasAttachedProfile) {
            toast('Complete your profile to find referrals', {
                position: 'top-center',
                action: {
                    label: 'Complete profile',
                    onClick: () => navigate('/profile'),
                },
            });
            return;
        }
        if (networkFetchStatus === 'error') {
            retryNetworkFetch();
            setIsReferralFabLoading(true);
            setPendingReferralScroll(true);
            referralClickTsRef.current = Date.now();
            return;
        }

        networkCaptureForClickDoneRef.current = false;

        // If results and profile are already here, switch instantly.
        if (profileLoaded && networkFetchStatus === 'success' && !networkLoading) {
            setShowNetworkView(true);
            scrollToBottomAnchor(false);
            setIsReferralFabLoading(false);
            setPendingReferralScroll(false);

        } else {
            setIsReferralFabLoading(true);
            setPendingReferralScroll(true);
            referralClickTsRef.current = Date.now();
        }
    }, [hasAttachedProfile, navigate, networkFetchStatus, networkLoading, profileLoaded, scrollToBottomAnchor, retryNetworkFetch]);
    useEffect(() => {
        if (!pendingReferralScroll) return;
        if (!profileLoaded) return;
        if (!hasAttachedProfile) {
            toast('Complete your profile to find referrals', {
                position: 'top-center',
                action: {
                    label: 'Complete profile',
                    onClick: () => navigate('/profile'),
                },
            });
            setIsReferralFabLoading(false);
            setPendingReferralScroll(false);
            return;
        }
        if (networkLoading || networkFetchStatus === 'loading' || networkFetchStatus === 'idle') return;
        if (networkFetchStatus === 'error') {
            toast.error(networkFetchError || 'Network request timed out. Please try again.');
            setIsReferralFabLoading(false);
            setPendingReferralScroll(false);
            return;
        }
        if (networkFetchStatus !== 'success') {
            setIsReferralFabLoading(false);
            setPendingReferralScroll(false);
            return;
        }
        const minDelayMs = 800;
        const elapsed = Date.now() - referralClickTsRef.current;
        const delay = Math.max(0, minDelayMs - elapsed);
        const t = setTimeout(() => {
            setShowNetworkView(true);
            scrollToBottomAnchor(false);
            setIsReferralFabLoading(false);
            setPendingReferralScroll(false);
        }, delay);
        return () => clearTimeout(t);
    }, [pendingReferralScroll, hasAttachedProfile, networkFetchError, networkFetchStatus, networkLoading, scrollToBottomAnchor, navigate, profileLoaded]);
    // useEffect(() => {
    //     return () => {
    //         if (referralFallbackTimerRef.current) {
    //             clearTimeout(referralFallbackTimerRef.current);
    //             referralFallbackTimerRef.current = null;
    //         }
    //     };
    // }, []);
    /* ── guard states ── */
    // If we're auto-navigating and the transition overlay is already showing, 
    // skip the dashboard loader entirely to avoid flashing.
    if (authLoading || (params.autoNavigateToBlock && !navTransition.active)) {
        return (
            <div className={appCls} style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgPage }}>
                <Loader2 size={36} style={{ color: accentColor, animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }
    return (
        <div className={`${appCls} compact-ui ${isMobileLayout ? 'company-insights-mobile' : ''}`} style={{ minHeight: '100vh', paddingTop: 56, fontFamily: "'Manrope', sans-serif" }}>
            {/* ── HEADER ── */}
            <header style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
                background: bgPage, backdropFilter: 'blur(16px)',
                borderBottom: `1px solid ${borderSub}`,
                padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: isDark ? 'rgba(255,255,255,0.6)' : '#475569', fontSize: 13
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = isDark ? '#fff' : '#0f172a'}
                        onMouseLeave={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.6)' : '#475569'}
                    >
                        <ArrowLeft size={15} />
                        <span>Back</span>
                    </button>
                    <div style={{ width: 1, height: 20, background: isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <div style={{
                            width: 32, height: 32,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: '#fff', borderRadius: '6px', padding: '4px'
                        }}>
                            <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: isDark ? '#fff' : '#0f172a' }}>fairhire</span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <FeedbackHeaderCTA />
                    {/* <button
                        onClick={handleAggregatedExport}
                        disabled={isExportingAggregated}
                        style={{
                            padding: '8px 12px',
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                            border: isDark ? '1px solid rgba(245,158,11,0.35)' : '1px solid rgba(37,99,235,0.35)',
                            background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)',
                            color: isDark ? '#FCD34D' : '#1d4ed8',
                            cursor: isExportingAggregated ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            opacity: isExportingAggregated ? 0.75 : 1,
                        }}
                    >
                        {isExportingAggregated ? (
                            <>
                                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                Preparing export...
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} />
                                Export matched jobs (temp)
                            </>
                        )}
                    </button> */}
                    <ThemeToggle />
                    {/*
                    <button
                        onClick={goToDashboard}
                        className="transition-map-button"
                        style={{
                            padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.75)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}
                    >
                        Full transition map <ArrowRight size={13} />
                    </button>
                    */}
                    {/* profile menu */}
                    <div ref={profileMenuRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setIsProfileMenuOpen(v => !v)}
                            style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(59,130,246,0.1)',
                                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(59,130,246,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                            }}
                        >
                            <UserIcon size={15} color={isDark ? 'rgba(255,255,255,0.75)' : '#2563eb'} />
                        </button>
                        <AnimatePresence>
                            {isProfileMenuOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                                    style={{
                                        position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                        width: 200,
                                        background: isDark ? 'rgba(13,16,25,0.98)' : '#ffffff',
                                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #dbe7fb',
                                        borderRadius: 14,
                                        padding: 8,
                                        boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 12px 36px rgba(37,99,235,0.18)'
                                    }}
                                >
                                    <div style={{ padding: '8px 12px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', marginBottom: 4 }}>
                                        <p style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', marginBottom: 2 }}>Signed in as</p>
                                        <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#fff' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</p>
                                    </div>
                                    {[
                                        { label: 'Profile', icon: <UserIcon size={14} />, onClick: () => { setIsProfileMenuOpen(false); navigate('/profile'); }, color: isDark ? 'rgba(255,255,255,0.75)' : '#334155' },
                                        { label: 'Logout', icon: <LogOut size={14} />, onClick: () => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }, color: '#f87171' },
                                    ].map(item => (
                                        <button
                                            key={item.label}
                                            onClick={item.onClick}
                                            style={{
                                                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none',
                                                color: item.color, fontSize: 13, cursor: 'pointer'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                        >
                                            {item.icon}{item.label}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </header>
            <AnimatePresence>
                {navTransition.active && (
                    <motion.div
                        initial={{ opacity: params.autoNavigateToBlock ? 1 : 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1000,
                            background: isDark ? 'rgba(13,16,25,0.97)' : 'rgba(255,255,255,0.98)',
                            backdropFilter: 'blur(8px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '24px',
                            textAlign: 'center',
                        }}
                    >
                        <motion.div
                            initial={{ y: 0, scale: 1 }}
                            animate={{ y: [0, 0, -170], scale: [1, 1, 0.92] }}
                            transition={{ duration: 1.5, times: [0, 0.55, 1], ease: 'easeInOut' }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            {navTransition.chip && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.92 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.24 }}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        borderRadius: 999,
                                        padding: '6px 12px',
                                        marginBottom: 18,
                                        background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.1)',
                                        border: isDark ? '1px solid rgba(245,158,11,0.26)' : '1px solid rgba(37,99,235,0.2)',
                                        color: accentColor,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor }} />
                                    {navTransition.chip}
                                </motion.div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, maxWidth: 640 }}>
                                {String(navTransition.title || '').split(' ').map((word, idx) => (
                                    <motion.span
                                        key={`${word}-${idx}`}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.06, duration: 0.38 }}
                                        style={{
                                            fontSize: 'clamp(20px, 2.6vw, 34px)',
                                            fontWeight: 800,
                                            color: idx % 4 === 0 ? accentColor : (isDark ? '#fff' : '#0f172a'),
                                            fontFamily: "'Playfair Display', serif",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {word}
                                    </motion.span>
                                ))}
                            </div>
                            {navTransition.subtitle && (
                                <motion.p
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.35, duration: 0.4 }}
                                    style={{
                                        marginTop: 10,
                                        color: isDark ? 'rgba(255,255,255,0.55)' : '#64748b',
                                        fontSize: 12,
                                        letterSpacing: '0.02em',
                                    }}
                                >
                                    {navTransition.subtitle}
                                </motion.p>
                            )}
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.3 }}
                            style={{
                                width: 220,
                                height: 4,
                                borderRadius: 999,
                                background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                                marginTop: 20,
                                overflow: 'hidden',
                            }}
                        >
                            <motion.div
                                initial={{ width: '0%' }}
                                animate={{ width: '100%' }}
                                transition={{ duration: 1.45, ease: 'easeInOut' }}
                                style={{
                                    height: '100%',
                                    borderRadius: 999,
                                    background: isDark
                                        ? 'linear-gradient(90deg, rgba(245,158,11,0.4), #F59E0B)'
                                        : 'linear-gradient(90deg, rgba(96,165,250,0.5), #2563eb)',
                                }}
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 0, 1] }}
                            transition={{ duration: 1.6, times: [0, 0.6, 1], ease: 'easeOut' }}
                            style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: '46%',
                                background: isDark
                                    ? 'linear-gradient(to top, rgba(255,255,255,0.04), transparent)'
                                    : 'linear-gradient(to top, rgba(15,23,42,0.04), transparent)',
                                pointerEvents: 'none',
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            {/* ── HERO HEADLINE ── */}
            <div
                className="company-insights-content-shell"
                style={{
                    width: '100%',
                    maxWidth: isMobileLayout ? 'none' : 1100,
                    margin: isMobileLayout ? '0' : '0 auto',
                    padding: isMobileLayout ? '34px 24px 0' : '34px 24px 0',
                }}
            >
                <div className="insights-hero-summary-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
                    <div className="insights-hero-copy">
                        <p style={{ fontSize: 12, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
                            {companyName} alumni · {startYear}–{endYear}
                        </p>
                        <h1 style={{
                            fontSize: 32, fontWeight: 800, color: isDark ? '#fff' : '#0f172a',
                            lineHeight: 1.2, marginBottom: 12, fontFamily: "'Playfair Display', serif"
                        }}>
                            {' '}
                            {loading ? (
                                <span style={{ color: isDark ? '#F59E0B' : '#2563eb' }}>… alumni</span>
                            ) : (
                                <span style={{ color: isDark ? '#F59E0B' : '#2563eb' }}>{alumniCount} {companyName} alumni</span>
                            )}{' '}
                            have moved.
                            <br />
                            {/* <br />Find the fastest path from {companyName} → your next job */}
                        </h1>
                        <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', lineHeight: 1.5 }}>
                            Find where alumni moved, which companies kept them longest, and who can refer you.
                        </p>
                    </div>
                    {!loading && alumniCount > 0 && (
                        <div className="insights-hero-stat-card" style={{
                            textAlign: 'left', background: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(59,130,246,0.05)',
                            border: isDark ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(59,130,246,0.15)',
                            borderRadius: 14, padding: '12px 18px'
                        }}>
                            <p style={{ fontSize: 32, fontWeight: 900, color: isDark ? '#F59E0B' : '#2563eb', lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>{alumniCount}</p>
                            <p style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', marginTop: 4 }}>alumni tracked</p>
                        </div>
                    )}
                </div>
                {!loading && !error && showProgressNudgeCard && (
                    <div className="insights-progress-card" style={{
                        marginBottom: 18,
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)',
                        border: `1px solid ${borderSub}`,
                        borderRadius: 14,
                        padding: isProgressNudgeOpen ? '12px 14px' : '10px 14px',
                    }}>
                        <button
                            onClick={() => setIsProgressNudgeOpen(v => !v)}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            {!isProgressNudgeOpen ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                                    <span style={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: '50%',
                                        background: progressNudge.color,
                                        display: 'inline-block',
                                        flexShrink: 0,
                                        opacity: 0.9,
                                        animation: 'blink 1.4s ease-in-out infinite',
                                    }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                        {progressItems.map((item) => {
                                            const radius = 8;
                                            const circumference = 2 * Math.PI * radius;
                                            const ratio = clamp(item.avg > 0 ? item.value / item.avg : 0, 0, 1);
                                            const dashOffset = circumference * (1 - ratio);
                                            return (
                                                <svg key={`compact-${item.label}`} width="30" height="30" viewBox="0 0 30 30">
                                                    <circle cx="15" cy="15" r={radius} fill="none" stroke={isDark ? 'rgba(255,255,255,0.14)' : '#e8ecf6'} strokeWidth="4" />
                                                    <circle cx="15" cy="15" r={radius} fill="none" stroke={progressNudge.color} strokeWidth="4" strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" transform="rotate(-90 15 15)" />
                                                </svg>
                                            );
                                        })}
                                    </div>
                                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.35, color: nudgeTextColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <span style={{ color: progressNudge.color, fontWeight: 700 }}>{progressCounts.apps}/{APP_AVG} applications · {progressCounts.referrals}/{REF_AVG} referrals</span> — {progressCompactSuffix}
                                    </p>
                                </div>
                            ) : (
                                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: nudgeHeadingColor }}>
                                    Applications & Referrals Progress
                                </span>
                            )}
                            {isProgressNudgeOpen ? <ChevronUp size={16} color={nudgeHeadingColor} /> : <ChevronDown size={16} color={nudgeHeadingColor} />}
                        </button>
                        <AnimatePresence initial={false}>
                            {isProgressNudgeOpen && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{ duration: 0.2 }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                                            {progressItems.map((item) => {
                                                const radius = 15;
                                                const circumference = 2 * Math.PI * radius;
                                                const ratio = clamp(item.avg > 0 ? item.value / item.avg : 0, 0, 1);
                                                const dashOffset = circumference * (1 - ratio);
                                                return (
                                                    <div key={item.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <div style={{ position: 'relative', width: 40, height: 40 }}>
                                                            <svg width="40" height="40" viewBox="0 0 40 40">
                                                                <circle cx="20" cy="20" r={radius} fill="none" stroke={isDark ? 'rgba(255,255,255,0.14)' : '#e8ecf6'} strokeWidth="4" />
                                                                <circle cx="20" cy="20" r={radius} fill="none" stroke={isDark ? 'rgba(245,158,11,0.25)' : 'rgba(61,90,254,0.18)'} strokeWidth="4" strokeDasharray={circumference} strokeDashoffset={0} transform="rotate(-90 20 20)" />
                                                                <circle
                                                                    cx="20"
                                                                    cy="20"
                                                                    r={radius}
                                                                    fill="none"
                                                                    stroke={progressNudge.color}
                                                                    strokeWidth="4"
                                                                    strokeDasharray={circumference}
                                                                    strokeDashoffset={dashOffset}
                                                                    strokeLinecap="round"
                                                                    transform="rotate(-90 20 20)"
                                                                    style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.25,1,0.5,1), stroke 0.35s' }}
                                                                />
                                                            </svg>
                                                            <span style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 9.5,
                                                                fontWeight: 700,
                                                                color: isDark ? '#fff' : '#1a1a2e',
                                                                fontFamily: 'Manrope, sans-serif',
                                                                lineHeight: 1,
                                                                pointerEvents: 'none',
                                                            }}>
                                                                {item.value}/{item.avg}
                                                            </span>
                                                        </div>
                                                        <span style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.5)' : '#9aa3bb', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <p style={{
                                            margin: 0,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: nudgeTextColor,
                                            lineHeight: 1.45,
                                            flex: 1,
                                            minWidth: 220,
                                            display: 'flex',
                                            alignItems: 'center',
                                            minHeight: 40,
                                        }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: progressNudge.color, display: 'inline-block', marginRight: 6, animation: 'blink 1.4s ease-in-out infinite' }} />
                                            <span dangerouslySetInnerHTML={{ __html: progressNudge.message }} />
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
                {/* ── LOADING STATE ── */}
                {loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
                        <Loader2 size={36} style={{ color: accentColor, animation: 'spin 1s linear infinite' }} />
                        <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>Mapping alumni movements…</p>
                    </div>
                )}
                {/* ── ERROR ── */}
                {!loading && error && (
                    <div style={{ padding: '20px 24px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 14, color: '#FCA5A5', fontSize: 14, marginBottom: 32 }}>
                        {error}
                    </div>
                )}
                {/* ── NETWORK INSIGHTS VIEW (old — removed) ── */}
                {!loading && !error && data && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(37,99,235,0.12)',
                            marginBottom: 18,
                        }}
                    >
                        <button
                            onClick={() => setShowNetworkView(false)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '12px 10px 14px',
                                border: 'none',
                                borderBottom: !showNetworkView
                                    ? `2px solid ${isDark ? '#3B82F6' : '#2563eb'}`
                                    : '2px solid transparent',
                                background: 'transparent',
                                color: !showNetworkView
                                    ? (isDark ? '#60A5FA' : '#2563eb')
                                    : (isDark ? 'rgba(255,255,255,0.7)' : '#64748b'),
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            <Briefcase size={16} />
                            Opportunities
                        </button>
                        <button
                            onClick={() => {
                                if (!dismissedNewTags.final_refresh) {
                                    const next = { ...dismissedNewTags, final_refresh: true };
                                    setDismissedNewTags(next);
                                    writeDismissedNewTags(user?.id, next);
                                }
                                handleFindReferralsClick();
                            }}
                            disabled={isReferralFabLoading}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '12px 10px 14px',
                                border: 'none',
                                borderBottom: showNetworkView
                                    ? `2px solid ${isDark ? '#3B82F6' : '#2563eb'}`
                                    : '2px solid transparent',
                                background: 'transparent',
                                color: showNetworkView
                                    ? (isDark ? '#60A5FA' : '#2563eb')
                                    : (isDark ? 'rgba(255,255,255,0.7)' : '#64748b'),
                                fontSize: 14,
                                fontWeight: 700,
                                cursor: isReferralFabLoading ? 'wait' : 'pointer',
                                opacity: isReferralFabLoading ? 0.85 : 1,
                            }}
                        >
                            {isReferralFabLoading ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                            My network & referrals
                            {!dismissedNewTags.final_refresh && (
                                <FeatureNewBadge
                                    isDark={isDark}
                                    tooltip="Find people from your college and past companies who can give you a referral."
                                />
                            )}
                        </button>
                    </div>
                )}
                {/* ── INSIGHTS GRID ── */}
                {!loading && !error && data && (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="insights-grid"
                    >
                        {!showNetworkView && (
                            <>
                                {/* 1 ── ACTIVE HIRERS */}
                                <Card onClick={() => goToList('active_hirers')}>
                                    <IllustrationHiringNow />
                                    <CardContent>
                                        <CardTag label="Hiring right now" color={isDark ? '#10B981' : '#059669'} />
                                        <CardTitle
                                            subtitle={`They hired someone from ${companyName} recently. Your profile already fits what they want.`}
                                        >
                                            Apply before they stop
                                        </CardTitle>
                                        <InnerBox>
                                            {activeHirers.slice(0, 2).map((c, i) => (
                                                <CompanyRow
                                                    key={c.organization_id}
                                                    name={c.organization}
                                                    rightLabel={c.last_hire_label}
                                                    rightColor={isDark ? "#10B981" : "#059669"}
                                                />
                                            ))}
                                        </InnerBox>
                                        {activeHirers.length === 0 && (
                                            <EmptyCardState
                                                message="No active hires in the last 2 years"
                                                hint="This company may not have recently hired from here, or data is still being collected."
                                            />
                                        )}
                                        <SeeMore label="Find a contact inside" onClick={() => goToList('active_hirers')} />
                                    </CardContent>
                                </Card>
                                {/* 2 ── NEXT DESTINATIONS */}
                                <Card onClick={() => goToList('next_destinations')}>
                                    <IllustrationReferral />
                                    <CardContent>
                                        <CardTag label="Easy referral" color={isDark ? '#3B82F6' : '#2563eb'} />
                                        <CardTitle
                                            subtitle={`${companyName} alumni work here, you can ask for a referral. That skips the queue entirely.`}
                                        >
                                            Your people are already there
                                        </CardTitle>
                                        <InnerBox>
                                            {nextDestinations.slice(0, 2).map((c, i) => (
                                                <CompanyRow
                                                    key={c.organization_id}
                                                    name={c.organization}
                                                    rightLabel={`${c.count} alumni inside`}
                                                    rightColor={isDark ? "#F59E0B" : "#2563eb"}
                                                />
                                            ))}
                                        </InnerBox>
                                        {nextDestinations.length === 0 && (
                                            <EmptyCardState
                                                message="No transition destinations tracked yet"
                                                hint="Alumni movement data for this company is still being assembled."
                                            />
                                        )}
                                        <SeeMore label="Get a referral contact" onClick={() => goToList('next_destinations')} />
                                    </CardContent>
                                </Card>
                                {/* 3 ── CAREER PATHS (2nd & 3rd moves) */}
                                <Card onClick={() => goToList('career_paths')}>
                                    <IllustrationCareerPath />
                                    <CardContent>
                                        <CardTag label="Plan ahead" color={isDark ? '#8B5CF6' : '#7c3aed'} />
                                        <CardTitle
                                            subtitle={`${companyName} alumni reach these after first move, you can target it now or use it to plan your next move.`}
                                        >
                                            Where {companyName} alumni go after their first move
                                        </CardTitle>
                                        <InnerBox>
                                            {mergedCareerPaths.slice(0, 2).map((c, i) => (
                                                <CompanyRow
                                                    key={c.organization_id}
                                                    name={c.organization}
                                                    rightLabel={`${c.count} people`}
                                                    rightColor={isDark ? "#8B5CF6" : "#7c3aed"}
                                                />
                                            ))}
                                        </InnerBox>
                                        {mergedCareerPaths.length === 0 && (
                                            <EmptyCardState
                                                message="No multi-hop paths tracked yet"
                                                hint="Stepping stone data requires 2+ moves tracked from alumni — still building."
                                            />
                                        )}
                                        <SeeMore label="Explore stepping stone paths" onClick={() => goToList('career_paths')} />
                                    </CardContent>
                                </Card>
                                {/* 4 ── BEST TENURE */}
                                <Card onClick={() => goToList('best_tenure')}>
                                    <IllustrationTenure />
                                    <CardContent>
                                        <CardTag label="Stable workplace" color={isDark ? '#F59E0B' : '#d97706'} />
                                        <CardTitle
                                            subtitle="Long tenure means good managers and real growth, not just a fancy title that leads nowhere."
                                        >
                                            People didn't quit after 6 months
                                        </CardTitle>
                                        <InnerBox>
                                            {bestTenure.slice(0, 2).map((c, i) => (
                                                <CompanyRow
                                                    key={c.organization_id}
                                                    name={c.organization}
                                                    rightLabel={`avg ${c.avg_tenure_years} yrs`}
                                                    rightColor={isDark ? "#10B981" : "#059669"}
                                                />
                                            ))}
                                        </InnerBox>
                                        {bestTenure.length === 0 && (
                                            <EmptyCardState
                                                message="Not enough tenure data yet"
                                                hint="We track average tenure only when multiple alumni have joined and stayed at a company."
                                            />
                                        )}
                                        <SeeMore label="See all stable companies" onClick={() => goToList('best_tenure')} />
                                    </CardContent>
                                </Card>
                                {/* 5 ── ROLE HIRING */}
                                <Card onClick={() => goToList('role_hiring', { selectedRole })}>
                                    <IllustrationRoleFunnel roleLabel={selectedRole || 'Product'} />
                                    <CardContent>
                                        <CardTag label="Filter by role" color={isDark ? '#3B82F6' : '#2563eb'} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <h3 style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.6 }}>
                                                Top companies hiring{' '}
                                                <button
                                                    onClick={e => { e.stopPropagation(); setShowRolePicker(true); }}
                                                    style={{
                                                        color: isDark ? '#F59E0B' : '#2563eb',
                                                        background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.1)',
                                                        border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(59,130,246,0.25)',
                                                        borderRadius: 6,
                                                        padding: '2px 8px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                                        display: 'inline-flex', alignItems: 'center', gap: 4, transform: 'translateY(-1px)'
                                                    }}
                                                >
                                                    {selectedRole || 'Product'} <ChevronDown size={12} />
                                                </button>
                                                {' '} from {companyName}
                                            </h3>
                                            {roleLoading && (
                                                <Loader2 size={14} style={{ color: isDark ? '#F59E0B' : '#2563eb', animation: 'spin 1s linear infinite', marginLeft: 10, flexShrink: 0 }} />
                                            )}
                                        </div>
                                        <p style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', lineHeight: 1.5, marginBottom: 16 }}>
                                            Not every company hires every role. These are the ones actually hiring {rolePlural} from {companyName}.
                                        </p>
                                        <InnerBox>
                                            {(selectedRole ? roleHiringRows : nextDestinations).slice(0, 2).map((c, i) => (
                                                <div key={c.organization_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <CompanyRow
                                                            name={c.organization}
                                                            rightLabel={`${c.count} ${selectedRole ? (selectedRole + 's').replace('ss', 's') + ' hired' : 'people'}`}
                                                            rightColor={isDark ? "#3B82F6" : "#2563eb"}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </InnerBox>
                                        {(selectedRole ? roleHiringRows : nextDestinations).length === 0 && (
                                            <EmptyCardState
                                                message={selectedRole ? `No ${selectedRole} hires found from ${companyName}` : `No ${selectedRole || 'Product'} hires found from ${companyName}`}
                                                hint={selectedRole ? 'Try a different role title — variations like "PM" vs "Product Manager" can yield different results.' : 'Try selecting a specific role title to filter hiring data more deeply.'}
                                            />
                                        )}
                                        <SeeMore
                                            label={`See companies hiring for your role`}
                                            onClick={() => goToList('role_hiring', { selectedRole })}
                                        />
                                    </CardContent>
                                </Card>
                                {/* 5.5 ── LOCATION HIRING */}
                                <Card onClick={() => goToList('location_hiring', { selectedLocation })}>
                                    <IllustrationLocation />
                                    <CardContent>
                                        <CardTag label="Filter by location" color={isDark ? '#0ea5e9' : '#0284c7'} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <h3 style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.6 }}>
                                                Moving to{' '}
                                                <button
                                                    onClick={e => { e.stopPropagation(); setShowLocationPicker(true); }}
                                                    style={{
                                                        color: isDark ? '#10B981' : '#059669',
                                                        background: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)',
                                                        border: isDark ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(16,185,129,0.2)',
                                                        borderRadius: 6,
                                                        padding: '2px 8px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                                        display: 'inline-flex', alignItems: 'center', gap: 4, transform: 'translateY(-1px)'
                                                    }}
                                                >
                                                    {selectedLocation || 'Bangalore'} <ChevronDown size={12} />
                                                </button>
                                                ? {companyName} people already landed here
                                            </h3>
                                            {locationLoading && (
                                                <Loader2 size={14} style={{ color: isDark ? '#10B981' : '#059669', animation: 'spin 1s linear infinite', marginLeft: 10, flexShrink: 0 }} />
                                            )}
                                        </div>
                                        <p style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', lineHeight: 1.5, marginBottom: 12 }}>
                                            Companies in {selectedLocation || 'Bangalore'} that have hired ex-{companyName} people before.
                                        </p>
                                        <InnerBox>
                                            {(selectedLocation ? locationHiringRows : nextDestinations).slice(0, 2).map((c, i) => (
                                                <CompanyRow
                                                    key={c.organization_id}
                                                    name={c.organization}
                                                    rightLabel={`${c.count} people`}
                                                    rightColor={isDark ? "#10B981" : "#059669"}
                                                />
                                            ))}
                                        </InnerBox>
                                        {(selectedLocation ? locationHiringRows : nextDestinations).length === 0 && (
                                            <EmptyCardState
                                                message={`No hires found in ${selectedLocation || 'Bangalore'} from ${companyName}`}
                                                hint="Try selecting a different city or check back later as we aggregate more alumni movement data."
                                            />
                                        )}
                                        <SeeMore
                                            label={`See companies hiring for your location`}
                                            color="#10B981"
                                            onClick={() => goToList('location_hiring', { selectedLocation })}
                                        />
                                    </CardContent>
                                </Card>
                            </>
                        )}
                        {showNetworkView && (
                            <>
                                {/* 6 ── NETWORK / MATCH PROFILE CTA */}
                                <Card
                                    style={{
                                        background: (profile && profileLength > 0)
                                            ? (isDark
                                                ? 'linear-gradient(180deg, rgba(30,41,59,0.78) 0%, rgba(15,23,42,0.82) 100%)'
                                                : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)')
                                            : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(59,130,246,0.02)'),
                                        border: (profile && profileLength > 0)
                                            ? (isDark ? '1px solid rgba(148,163,184,0.30)' : '1px solid #dbeafe')
                                            : (isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(59,130,246,0.08)'),
                                        position: 'relative', overflow: 'hidden', height: '100%',
                                        gridColumn: showNetworkView ? '1 / -1' : undefined,
                                    }}
                                >
                                    <CardContent style={{ padding: showNetworkView ? '22px 24px 24px' : undefined }}>
                                        {!(profile && profileLength > 0) && (
                                            /* Decorative blurred glow spot in top-right corner */
                                            <div style={{
                                                position: 'absolute', top: -30, right: -30,
                                                width: 120, height: 120,
                                                borderRadius: '50%',
                                                background: isDark ? 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
                                                pointerEvents: 'none',
                                            }} />
                                        )}
                                        {/* Icon row */}
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{
                                                    width: 42, height: 42, borderRadius: 12,
                                                    background: (profile && profileLength > 0) ? (isDark ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.12)') : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.04)'),
                                                    border: (profile && profileLength > 0) ? (isDark ? '1px solid rgba(96,165,250,0.32)' : '1px solid rgba(59,130,246,0.24)') : (isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(59,130,246,0.08)'),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    position: 'relative',
                                                    flexShrink: 0
                                                }}>
                                                    <Network size={20} color={(profile && profileLength > 0) ? (isDark ? '#60A5FA' : '#2563eb') : (isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8')} />
                                                    {!(profile && profileLength > 0) && (
                                                        <div style={{
                                                            position: 'absolute', bottom: -3, right: -3,
                                                            width: 14, height: 14, borderRadius: '50%',
                                                            background: isDark ? '#0d1019' : '#ffffff',
                                                            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            <Lock size={7} color={isDark ? 'rgba(255,255,255,0.45)' : '#64748b'} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    {(profile && profileLength > 0) ? (
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            width: 'fit-content',
                                                            padding: '4px 10px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                                                            background: isDark ? 'rgba(59,130,246,0.16)' : 'rgba(37,99,235,0.10)',
                                                            border: isDark ? '1px solid rgba(96,165,250,0.35)' : '1px solid rgba(37,99,235,0.22)',
                                                            color: isDark ? '#93C5FD' : '#1d4ed8', letterSpacing: '0.06em'
                                                        }}>PROFILE CONNECTED</span>
                                                    ) : !profileLoaded ? (
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            width: 'fit-content',
                                                            padding: '4px 10px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                                                            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.04)',
                                                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(59,130,246,0.08)',
                                                            color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8', letterSpacing: '0.06em'
                                                        }}>LOADING...</span>
                                                    ) : (
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            width: 'fit-content',
                                                            padding: '4px 10px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                                                            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.04)',
                                                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(59,130,246,0.08)',
                                                            color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b', letterSpacing: '0.06em'
                                                        }}>LOCKED</span>
                                                    )}
                                                    <p style={{ margin: 0, fontSize: showNetworkView ? 16 : 14, fontWeight: 700, color: isDark ? '#E2E8F0' : '#1e293b' }}>
                                                        Companies where your network can help you
                                                    </p>
                                                </div>
                                            </div>
                                            {(profile && profileLength > 0) && (
                                                <span style={{
                                                    flexShrink: 0,
                                                    padding: '5px 10px',
                                                    borderRadius: 999,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    background: isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.10)',
                                                    border: isDark ? '1px solid rgba(52,211,153,0.3)' : '1px solid rgba(16,185,129,0.22)',
                                                    color: isDark ? '#6EE7B7' : '#047857'
                                                }}>
                                                    Warm referral paths
                                                </span>
                                            )}
                                        </div>
                                        <p style={{ fontSize: showNetworkView ? 14 : 12, color: isDark ? 'rgba(255,255,255,0.78)' : '#475569', lineHeight: 1.65, marginBottom: 12 }}>
                                            Among companies {companyName} people moved to, these are the companies where people from your past companies and college already work.
                                        </p>
                                        <div className="profile-connected-chips" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                                            {[
                                                'People already know your background',
                                                'Faster intros than cold outreach',
                                                'Best place to start referral asks',
                                            ].map((hint) => (
                                                <span
                                                    key={hint}
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        padding: '4px 8px',
                                                        borderRadius: 999,
                                                        background: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(226,232,240,0.8)',
                                                        color: isDark ? 'rgba(226,232,240,0.9)' : '#475569',
                                                        border: isDark ? '1px solid rgba(148,163,184,0.24)' : '1px solid #e2e8f0',
                                                    }}
                                                >
                                                    {hint}
                                                </span>
                                            ))}
                                        </div>
                                        {/* mini stats preview - Always show layout, blur if loading/locked */}
                                        <div className="profile-connected-stats" style={{
                                            marginTop: 'auto', display: 'flex', gap: 8,
                                            filter: (networkLoading || networkFetchStatus !== 'success' || !profileLoaded || !data || !profile || profileLength === 0) ? 'blur(6px)' : 'none',
                                            transition: 'filter 0.5s ease-out',
                                            pointerEvents: (networkLoading || networkFetchStatus !== 'success' || !profileLoaded || !data || !profile || profileLength === 0) ? 'none' : 'auto',
                                            marginBottom: 16
                                        }}>
                                            {[
                                                { val: Number(data?.network_work_total || 0), label: 'Ex-colleagues', color: '#34D399', bg: 'rgba(52,211,153,0.04)', icon: <Users size={13} /> },
                                                { val: Number(data?.network_education_total || 0), label: 'Classmates', color: '#8B5CF6', bg: 'rgba(139,92,246,0.04)', icon: <GraduationCap size={13} /> },
                                                { val: Number(data?.network_cross_total || 0), label: 'Shared Work & College', color: '#F59E0B', bg: 'rgba(245,158,11,0.04)', icon: <Briefcase size={13} /> }
                                            ].map((stat, sIdx) => (
                                                <div key={sIdx} style={{
                                                    flex: 1,
                                                    background: isDark ? 'rgba(15,23,42,0.55)' : '#f8fafc',
                                                    border: isDark ? '1px solid rgba(148,163,184,0.24)' : '1px solid #e2e8f0',
                                                    borderRadius: 12,
                                                    padding: showNetworkView ? '14px 16px' : '10px 10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    minWidth: 0,
                                                }}>
                                                    <div style={{
                                                        width: showNetworkView ? 40 : 34,
                                                        height: showNetworkView ? 40 : 34,
                                                        borderRadius: 12,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: stat.color,
                                                        background: isDark ? `${stat.color}22` : `${stat.color}1A`,
                                                        border: `1px solid ${isDark ? `${stat.color}40` : `${stat.color}33`}`,
                                                        flexShrink: 0,
                                                    }}>
                                                        {React.cloneElement(stat.icon, { size: showNetworkView ? 18 : 14 })}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                                                        <p style={{ margin: 0, fontSize: showNetworkView ? 22 : 18, fontWeight: 900, color: stat.color, lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>
                                                            {(networkLoading || networkFetchStatus !== 'success' || !profileLoaded || !data || !profile || profileLength === 0) ? '??' : stat.val}
                                                        </p>
                                                        <p style={{ margin: '6px 0 0', fontSize: showNetworkView ? 11 : 10, color: isDark ? 'rgba(255,255,255,0.6)' : '#475569', lineHeight: 1.2 }}>
                                                            {stat.label}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {!profileLoaded ? (
                                            <button
                                                disabled
                                                style={{
                                                    padding: '12px 20px', borderRadius: 12,
                                                    background: isDark
                                                        ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                                                        : 'linear-gradient(135deg, #3B82F6, #2563EB)',
                                                    opacity: 0.7,
                                                    color: isDark ? '#1C1917' : '#ffffff', fontWeight: 800, fontSize: 13,
                                                    border: 'none', cursor: 'wait', width: '100%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                                                }}
                                            >
                                                <Loader2 size={16} className="animate-spin" /> See my network inside
                                            </button>
                                        ) : (profile && profileLength > 0) ? (
                                            <motion.button
                                                onClick={handleFindReferralsClick}
                                                initial={false}
                                                whileHover={{ y: -1 }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    padding: '12px 20px', borderRadius: 12,
                                                    background: isDark
                                                        ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                                                        : 'linear-gradient(135deg, #3B82F6, #2563EB)',
                                                    color: isDark ? '#1C1917' : '#ffffff', fontWeight: 800, fontSize: 13,
                                                    border: 'none', cursor: 'pointer', width: '100%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                                                }}
                                            >
                                                {isReferralFabLoading ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" /> Finding your network...
                                                    </>
                                                ) : (
                                                    <>
                                                        See my network inside <ArrowRight size={16} />
                                                    </>
                                                )}
                                            </motion.button>
                                        ) : (
                                            <motion.button
                                                onClick={() => navigate('/profile')}
                                                initial={false}
                                                whileHover={{ y: -1 }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    padding: '12px 20px', borderRadius: 12,
                                                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                                    color: '#1C1917', fontWeight: 800, fontSize: 13,
                                                    border: 'none', cursor: 'pointer', width: '100%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                                                }}
                                            >
                                                <Lock size={14} /> Add profile to unlock
                                            </motion.button>
                                        )}
                                    </CardContent>
                                </Card>
                                {/* ── INLINE NETWORK SECTION (part of the grid when profile exists) ── */}
                                {!loading && !error && data && profile && profileLength > 0 && showNetworkView && (
                                    (networkLoading || networkFetchStatus === 'loading' || networkFetchStatus === 'idle') ? (
                                        <Card style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, gridColumn: '1 / -1' }}>
                                            <Loader2 size={20} style={{ color: '#8B5CF6', animation: 'spin 1s linear infinite' }} />
                                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', margin: 0 }}>Loading your network & referral sections…</p>
                                        </Card>
                                    ) : networkFetchStatus === 'success' ? (
                                        <NetworkSection
                                            ref={networkSectionRef}
                                            companyName={companyName}
                                            goToList={goToList}
                                            crossCompanies={Array.isArray(data?.network_cross_companies) ? data.network_cross_companies : []}
                                            workTabsData={(data?.network_work_tabs && typeof data.network_work_tabs === 'object') ? data.network_work_tabs : {}}
                                            profileWorkCompanies={profileWorkCompanies}
                                            collegeTabsData={(data?.network_education_tabs && typeof data.network_education_tabs === 'object') ? data.network_education_tabs : {}}
                                            profileColleges={profileColleges}
                                            profileLoaded={profileLoaded}
                                        />
                                    ) : null
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </div>
            {/* ── FOOTER STRIP ── */}
            {/*
            {!loading && !error && data && (
                <div className="insights-footer" style={{
                    position: 'sticky', bottom: 0, background: 'rgba(13,16,25,0.95)',
                    backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.07)',
                    padding: '12px 24px'
                }}>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                        <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
                            Not finding what you're looking for? Open the full transition map
                        </span>{' '}
                        — explore {data.next_destinations.length}+ destination companies with your own filters.
                    </p>
                    <button
                        onClick={goToDashboard}
                        style={{
                            padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
                            color: 'rgba(255,255,255,0.8)', cursor: 'pointer', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: 6
                        }}
                    >
                        I'll explore myself <ArrowRight size={13} />
                    </button>
                </div>
            )}
            */}
            {/* ── ROLE AND LOCATION PICKER MODALS ── */}
            <AnimatePresence>
                {showRolePicker && (
                    <RolePickerModal
                        available={data?.available_roles || []}
                        currentRole={selectedRole}
                        onSelect={handleRoleSelect}
                        onClose={() => setShowRolePicker(false)}
                    />
                )}
                {showLocationPicker && (
                    <LocationPickerModal
                        available={data?.available_locations || []}
                        currentLocation={selectedLocation}
                        onSelect={handleLocationSelect}
                        onClose={() => setShowLocationPicker(false)}
                    />
                )}
            </AnimatePresence>
            <div ref={bottomAnchorRef} style={{ height: 1 }} />
            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.1; } }
                @keyframes dotBloom {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 currentColor; }
                    70% { transform: scale(1.12); box-shadow: 0 0 0 8px transparent; }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 transparent; }
                }
                
                .insights-grid {
                    display: grid;
                    grid-template-columns: repeat(1, minmax(0, 1fr));
                    gap: 16px;
                    padding-bottom: 44px;
                }
                
                @media (min-width: 640px) {
                    .insights-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }
                
                @media (min-width: 1024px) {
                    .insights-grid {
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                    }
                }
                @media (max-width: 720px) {
                    .company-insights-content-shell {
                        width: 100% !important;
                        max-width: none !important;
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                        padding: 34px 24px 0 !important;
                    }
                    .insights-hero-summary-row {
                        justify-content: flex-start !important;
                        gap: 14px !important;
                    }
                    .insights-hero-copy {
                        width: 100% !important;
                        max-width: 640px !important;
                    }
                    .insights-hero-stat-card {
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                        min-width: 118px !important;
                        text-align: left !important;
                    }
                    .insights-progress-card,
                    .insights-grid {
                        max-width: 820px !important;
                    }
                    .profile-connected-stats {
                        flex-direction: column !important;
                    }
                    .profile-connected-chips {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                    }
                }
                .company-insights-mobile .company-insights-content-shell {
                    width: 100% !important;
                    max-width: none !important;
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                    padding: 34px 24px 0 !important;
                }
                .company-insights-mobile .insights-hero-summary-row {
                    justify-content: flex-start !important;
                    gap: 14px !important;
                }
                .company-insights-mobile .insights-hero-copy {
                    width: 100% !important;
                    max-width: none !important;
                }
                .company-insights-mobile .insights-hero-stat-card {
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                    min-width: 118px !important;
                    text-align: left !important;
                }
                .company-insights-mobile .insights-progress-card,
                .company-insights-mobile .insights-grid {
                    width: 100% !important;
                    max-width: none !important;
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                }
                .network-divider {
                    grid-column: span 1;
                }
                
                @media (min-width: 640px) {
                    .network-divider {
                        grid-column: span 2;
                    }
                }
                
                @media (min-width: 1024px) {
                    .network-divider {
                        grid-column: span 3;
                    }
                }
                .insights-footer {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    align-items: center;
                    justify-content: space-between;
                }
                
                @media (min-width: 768px) {
                    .insights-footer {
                        flex-direction: row;
                        gap: 24px;
                    }
                }
                @media (max-width: 720px) {
                    .transition-map-button {
                        padding: 6px 12px !important;
                        font-size: 11px !important;
                        border-radius: 9px !important;
                        gap: 4px !important;
                    }
                }
                .find-referrals-fab-wrap {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 3px;
                    border-radius: 999px;
                    isolation: isolate;
                }
                .find-referrals-fab-wrap::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: inherit;
                    padding: 2px;
                    background: conic-gradient(
                        from var(--angle, 0deg),
                        transparent 0deg,
                        transparent 200deg,
                        var(--fab-shine-a, #A0C4FF) 240deg,
                        var(--fab-shine-a, #A0C4FF) 270deg,
                        var(--fab-shine-a, #A0C4FF) 300deg,
                        transparent 340deg,
                        transparent 360deg
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    mask-composite: exclude;
                    opacity: 0;
                    pointer-events: none;
                    animation: fabBorderSweep 2.5s linear infinite;
                    transition: opacity 180ms ease;
                }
                .find-referrals-fab-wrap.cta-shine::before {
                    opacity: 0.85;
                }
                .find-referrals-fab-wrap:hover::before {
                    opacity: 1;
                }
                .find-referrals-fab-btn {
                    position: relative;
                    z-index: 1;
                    min-width: 172px;
                    height: 48px;
                    padding: 0 14px 0 8px;
                    border-radius: 999px;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease;
                }
                .find-referrals-fab-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    flex-shrink: 0;
                }
                @property --angle {
                    syntax: '<angle>';
                    initial-value: 0deg;
                    inherits: false;
                }
                @keyframes fabBorderSweep {
                    to { --angle: 360deg; }
                }
                @media (max-width: 640px) {
                    .find-referrals-fab-wrap {
                        right: 12px !important;
                        bottom: 12px !important;
                    }
                    .find-referrals-fab-btn {
                        min-width: 156px !important;
                        height: 44px !important;
                    }
                }
            `}</style>
        </div >
    );
};
export default CompanyInsights;
